import { toMultiPolygonPositions } from "./utils/geoSpacePolygon";
import { SearchBox } from "./components/SearchBox";
import { CHIBAN_SEARCH_MAX_LENGTH } from "@/features/map/constants";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { MapControlContext, type MapMode } from "./contexts/MapControlContext";
import { HEADER_HEIGHT } from "@/features/header";
import { usePopupTagHandlers } from "./hooks/usePopupTagHandlers";
import {
  addOrFindMapClickProperty,
  type AddOrFindMapClickPropertyParams,
} from "./utils/propertySelection/addOrFindMapClickProperty";
import { isRowOfMapClickPin } from "./utils/propertySelection/isRowOfMapClickPin";
import {
  SAME_POSITION_MARGIN,
  getPropertyRowPosition,
} from "./utils/propertySelection/propertyRowPosition";
import {
  findClickPinAtPosition,
  findPopupPinAtPosition,
} from "./utils/propertySelection/findPopupPinAtPosition";
import {
  type KaokuSearchSource,
  withKaokuSearchSourcePosition,
} from "./utils/propertySelection/kaokuSearchSourcePosition";
import {
  findPropertyRowIdForPin,
  getPinProperty,
  type PinProperty,
} from "./utils/propertySelection/pinProperty";
import { generateNextId } from "./utils/propertySelection/generateNextId";
import {
  createLandAddressLoader,
  fillLandAddress,
} from "./utils/realEstateSearch/landAddress";
import {
  getRealEstateSearchAddressAPI,
  type RealEstateSearchAddressResult,
} from "./api/getRealEstateSearchAddress";
import { attachParcelPolygon } from "./utils/propertySelection/attachParcelPolygon";
import { schedulePanToPinAndPopup } from "./utils/popup/pinPopupFocus";
import { ensureReceptionBookPropertyRow } from "./utils/propertySelection/ensureReceptionBookPropertyRow";
import {
  type GeoSpaceChiban,
  type GeoSpacePolygonCoordinates,
  type GeoSpacePolygonItem,
  type MarkerState,
  type PropertySelectionRow,
  type ReceptionBook,
  type GeoSpaceReverseApiError,
  type RestorePropertyRow,
  type RealEstateSearchResult,
  type SavedMapPlotRequestDetail,
  type RealEstateDocumentChibanKaokuNumberRow,
  type ZenrinMapRequestHistoryResponse,
} from "./types";
import { type ChibanInput } from "@/types/kaokuNumber";
import type { IPopupBookContent, GetPopupBookResult } from "./types/popup";
import type { ChibanViewerProps } from "./types/component";
import {
  INITIAL_ZOOM,
  LeafletMap,
  type OverlayController,
} from "./components/LeafletMap";
import type L from "leaflet";
import { LatLng, type LatLngBounds, type Map } from "leaflet";
import { toast } from "react-toastify";
import {
  consumeZenrinRateLimitError,
  isZenrinRateLimitError,
  shouldWaitAndRetryZenrinRateLimit,
  showZenrinRateLimitToast,
  sleepMs,
  ZENRIN_RECEPTION_BOOK_PLOT_CONCURRENCY,
} from "@/features/map/utils/zenrinRateLimit";
import { runWithConcurrency } from "@/features/map/utils/runWithConcurrency";
import { useGeoSpaceApi } from "@/features/map/hooks/useGeospaceSearch";
import { useMapUrlParams } from "@/features/map/hooks/useMapUrlParams";
import {
  BLUE_MAP_LAYER_NAME,
  ZENRIN_RESIDENTIAL_LAYER_NAME,
} from "@/features/map/utils/urlParams/applyLayerParams";
import { dispatchAddressSearch } from "@/features/map/utils/urlParams/dispatchAddressSearch";
import { buildSearchChain } from "@/features/map/utils/buildSearchChain";
import { buildSearchSourceStates } from "@/features/map/utils/buildSearchSourceStates";
import {
  runSearchChain,
  RATE_LIMITED_MESSAGE,
  type ChainResolution,
} from "@/features/map/utils/runSearchChain";
import {
  useParcelSearchSources,
  REQUIRES_ADDRESS_SOURCES,
} from "@/features/map/hooks/useParcelSearchSources";
import {
  SEARCH_SOURCE_LABEL,
  GOOGLE_GEOCODE_SOURCE_LABEL,
} from "@/features/map/utils/searchSourceMeta";
import { SEARCH_SOURCE_LAYER } from "@/features/map/utils/searchSourceLayers";
import { notifySourceMismatch } from "@/features/map/utils/notifySourceMismatch";
import {
  BASE_INITIAL_LAYERS,
  ZENRIN_INITIAL_LAYERS,
  GEOSPACE_INITIAL_LAYERS,
  computeUrlBasedCheckedLayers,
} from "@/features/map/utils/urlParams/computeUrlBasedCheckedLayers";
import {
  useZenrinApi,
  zenrinToGeoSpaceChiban,
} from "@/features/map/hooks/useZenrinSearch";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { PopupContent } from "@/features/map/components/PopupContent";
import type {
  AcquireMultipleBookHandoffData,
  BookTypeEng,
} from "@/types/acquirebook";
import { useReceptionBookApi } from "@/features/map/hooks/useReceptionBook";
import {
  Button as MuiButton,
  Box,
  type SxProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Divider,
  Link,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { type LocationStateWithHandOff } from "@/types/handOffMessage";
import { PropertySelectionGrid } from "./components/PropertySelectionGrid";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { AcquireMultipleBooksModal } from "./components/AcquireMultipleBooksModal";
import { ChibanKaokuNumberSearchModal } from "@/components/KaokuNumberSearch/ChibanKaokuNumberSearchModal";
import { RealEstateDocumentFetchModal } from "@/features/map/components/RealEstateDocumentFetchModal";
import { useAcquireBookForm } from "@/features/acquireMultipleBooks/hooks/useAcquireBookForm";
import {
  GEOSPACE_CHIBAN_TILE_LAYER_NAME,
  LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME,
} from "@/features/map/components/CommonChibanTileLayer";
import { TRUSTART_CHIBAN_LAYER_NAME } from "@/features/map/components/layers/TrustartChibanTileLayer";
import { REGISTRATION_MAP_LAYER_NAME } from "@/features/map/components/mapLibre/layers/RegistrationMapTileLayer";
import { type GeocodedPoint } from "@/features/map/types/geocodedPoint";
import { GetPrefecturesAPI } from "@/api";
import { type IPrefecture } from "@/types/prefectures";
import { useApiClient } from "@/hooks/useApiClient";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSaleDocumentSearchFallback } from "@/features/map/hooks/useSaleDocumentSearchFallback";
import {
  CreatePinHistoryAPI,
  type CreatePinHistoryResponse,
} from "@/features/map/api/CreatePinHistoryAPI";
import { GetLatLngByAddressAPI } from "@/features/map/api/getLatLngByAddress";

import { useFeatureFlags } from "@/configs/featureFlag";
import Tooltip from "@mui/material/Tooltip";
import { useAdditionalFieldsConfig } from "./hooks/useAdditionalFieldsConfig";
import { useMapImageExport } from "./hooks/useMapImageExport";
import { type MapPrintOptions } from "./utils/printLayout";
import { normalizeLocation, normalizeChibanKaokuNumber } from "./utils";
import { LoadedMapPlotBar } from "./components/LoadedMapPlotBar";
import {
  type LoadedDataset,
  addLoadedDatasets,
  appendLoadedDatasets,
  removeLoadedDataset,
  toColorByRequestId,
} from "./utils/savedPlot/loadedDatasets";
import { useMapPlotRequest } from "./hooks/useMapPlotRequest";
import { exportPlotRequestDetails } from "@/utils/mapPlotRequestExport";
import { getPlotRequestDetailAPI } from "@/features/map/api/getPlotRequestDetailAPI";
import { MapSearchDialog } from "@/features/map/components/MapSearchDialog";
import { MapPlotRequestCsvUploadModal } from "@/features/mapPlotRequest/components/MapPlotRequestCsvUploadModal";
import { SavedPlotModal } from "./components/mapPlotRequest/SavedPlotModal";
import { MapPlotImportModal } from "./components/MapPlotImportModal";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import IconButton from "@mui/material/IconButton";
import { REAL_ESTATE_DOCUMENT_FETCH_MANUAL_URL } from "@/configs/constants";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NewPropertySummaryModal from "@/features/map/components/NewPropertySummaryModal";
import { type AddressIntegrator } from "@/utils/AddressIntegrator";
import { ZenrinNameplateButtonGroup } from "@/features/map/components/ZenrinNameplateButtonGroup";
import { ZenrinRequestHistoryModal } from "@/features/map/components/ZenrinRequestHistoryModal";
import { MapExportPanel } from "@/features/map/components/MapExportPanel";
import { zenrinComputeA3Bounds } from "@/features/map/utils/zenrinComputeA3Bounds";
import { TRADE_PRICE_LAYER_NAME } from "@/features/map/components/TradePriceLayerControlWrapper";
import { TradePriceListModal } from "./components/mapPlotRequest/TradePriceListModal";
import { searchTradePriceAPI } from "@/features/map/api/getTradePrice";
import {
  type TradePriceGridRow,
  type TradePriceTileFilter,
  type TradePriceListFilter,
  DEFAULT_TRADE_PRICE_TILE_FILTER,
  getDefaultTradePriceFilter,
} from "@/features/map/types/tradePrice";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import * as Sentry from "@sentry/react";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { useIsMobile } from "@/hooks/useIsMobile";
import "./components/styles/map-popup.css";
import {
  MAP_BORDER,
  MAP_BUTTON_HEIGHT,
  MAP_CONTROL_TEXT_SX,
  MAP_MENU_SX,
  MAP_TOOLBAR_BUTTON_SX,
} from "@/features/map/constants/controlSize";

/** 物件一覧の行を押して地図を動かす時、これより引いていたら寄せる（地番が読めるズーム） */
const FOCUS_PROPERTY_MIN_ZOOM = 18;

/** 地図画面のボタン（左の列・ツールバー・物件一覧の拡大モーダル）。白地に濃い枠、高さをそろえる */
const mapMenuButtonSx: SxProps<Theme> = (theme) => ({
  ...MAP_CONTROL_TEXT_SX,
  textTransform: "none",
  bgcolor: theme.palette.common.white,
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  "&:hover": {
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
  },
  height: MAP_BUTTON_HEIGHT,
});

// ---- 地図画面のレイアウト ----
// PC では左の操作列と右の地図を、ヘッダーを除いた画面の高さに収める（ページ全体はスクロールさせない）。
// 下には常に余白を残す。SP では縦に積み、地図には画面の高さの7割を割り当てる。
const MAP_PAGE_PADDING_TOP_PX = 12;
const MAP_PAGE_PADDING_BOTTOM_PX = 16;
/** これより低い画面では収めず、ページをスクロールさせる */
const MAP_PAGE_MIN_HEIGHT_PX = 520;

const MAP_PAGE_SX: SxProps<Theme> = {
  px: { xs: 1.5, [MOBILE_BREAKPOINT]: 2 },
  pt: `${MAP_PAGE_PADDING_TOP_PX}px`,
  pb: `${MAP_PAGE_PADDING_BOTTOM_PX}px`,
};

const MAP_PAGE_COLUMNS_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", [MOBILE_BREAKPOINT]: "row" },
  gap: 2,
  height: {
    [MOBILE_BREAKPOINT]: `calc(100dvh - ${
      HEADER_HEIGHT + MAP_PAGE_PADDING_TOP_PX + MAP_PAGE_PADDING_BOTTOM_PX
    }px)`,
  },
  minHeight: { [MOBILE_BREAKPOINT]: MAP_PAGE_MIN_HEIGHT_PX },
};

/** 左の操作列。幅は画面に合わせて伸ばすが、広い画面で広がりすぎないよう上限を付ける */
const MAP_SIDE_COLUMN_SX: SxProps<Theme> = {
  width: { xs: "100%", [MOBILE_BREAKPOINT]: "clamp(320px, 25vw, 400px)" },
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  minHeight: 0,
  // 入り切らない時は列の中だけスクロールさせる
  overflowY: { [MOBILE_BREAKPOINT]: "auto" },
  // 狭い列でボタンの文字が折り返さないよう、左右の余白を詰める（検索ボタンも文字の大きさをそろえる）
  "& .MuiButton-root": {
    ...MAP_CONTROL_TEXT_SX,
    px: 1,
  },
};

/** 条件・住所・地番の検索欄のまとまり */
const MAP_SIDE_SECTION_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

/** 左の列の見出し（条件・住所・地番で検索、物件リスト）。SearchBox のラベルと揃える */
const MAP_SIDE_HEADING_SX: SxProps<Theme> = {
  fontWeight: 700,
  color: "text.primary",
};

/** 住所の検索欄の下の行（この住所に対する操作と、その操作ガイド） */
const MAP_ADDRESS_ACTION_ROW_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  mt: 0.5,
};

/** 操作ガイドへのリンク。SP では指で押せる高さを取る */
const MAP_ADDRESS_ACTION_LINK_SX: SxProps<Theme> = {
  ...MAP_CONTROL_TEXT_SX,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.25,
  flexShrink: 0,
  minHeight: { xs: MIN_TAP_TARGET, [MOBILE_BREAKPOINT]: "auto" },
};

/**
 * 物件リスト（見出し・操作のボタン・物件一覧）。列の残りの高さを使う。
 * 中身（表の最小の高さ）より縮めない。入り切らない時は列ごとスクロールする
 */
const MAP_SIDE_LIST_SECTION_SX: SxProps<Theme> = {
  flex: "1 0 auto",
  display: "flex",
  flexDirection: "column",
};

/** 物件リストの見出しと拡大ボタンの行 */
const MAP_SIDE_LIST_HEADER_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 0.5,
};

/** 物件一覧の表。残りの高さいっぱいに広げ、低い画面でも見出しと数行は見えるようにする */
const MAP_SIDE_GRID_SX: SxProps<Theme> = {
  // SP は縦に積むので親の高さが決まらない。表の高さを直接決める
  height: { xs: 360, [MOBILE_BREAKPOINT]: "auto" },
  flex: { [MOBILE_BREAKPOINT]: 1 },
  minHeight: { [MOBILE_BREAKPOINT]: 240 },
};

const MAP_MAIN_COLUMN_SX: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

/** 地図。PC はツールバーの下の残り、SP は画面の高さの7割 */
const MAP_AREA_SX: SxProps<Theme> = {
  flex: { [MOBILE_BREAKPOINT]: 1 },
  minHeight: { [MOBILE_BREAKPOINT]: 0 },
  height: { xs: "max(420px, 70svh)", [MOBILE_BREAKPOINT]: "auto" },
};

const MAP_EXPORT_PANEL_COLUMN_SX: SxProps<Theme> = {
  width: { xs: "100%", [MOBILE_BREAKPOINT]: "clamp(320px, 25vw, 420px)" },
  flexShrink: 0,
  height: { [MOBILE_BREAKPOINT]: "100%" },
  overflowY: "auto",
};

/** 地図を使えないユーザー向けの、有料プランの申し込みフォーム */
const PAID_PLAN_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSc55YOK76TfgQyXEYUoXQS6eZIC25V2qwKdILOMxV87fBCdIQ/viewform?usp=pp_url&entry.1890122079=%E5%9C%B0%E5%9B%B3%E6%A4%9C%E7%B4%A2%E3%81%AE%E6%9C%89%E5%84%9F%E3%83%97%E3%83%A9%E3%83%B3%E3%81%AB%E8%88%88%E5%91%B3%E3%81%82%E3%82%8A%E2%80%BB%E6%96%87%E8%A8%80%E3%81%AF%E7%B7%A8%E9%9B%86%E3%81%97%E3%81%AA%E3%81%84%E3%81%A7%E3%81%8F%E3%81%A0%E3%81%95%E3%81%84";

/** 地図の上の帯（地図に対する操作）。地図の枠につなげる */
const MAP_AREA_TOOLBAR_SX: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 0.5,
  px: 0.5,
  py: 0.25,
  // 薄い地色で、この帯の操作は地図に効くと分かるようにする
  bgcolor: "#fbfcfd",
  border: MAP_BORDER,
  borderBottom: "none",
  // ゼンリンの範囲選択中のボタン群（高さ 40px 固定）も帯のボタンと高さをそろえる
  "& .MuiButtonGroup-root, & .MuiButtonGroup-root .MuiButton-root": {
    ...MAP_CONTROL_TEXT_SX,
    height: MAP_BUTTON_HEIGHT,
  },
};

const GEOSPACE_ELECTRONIC_MAP_LAYER_NAME = "GEOSPACE電子地図";
const MAP_ZENRIN_LAYER_NAMES = [
  ZENRIN_RESIDENTIAL_LAYER_NAME,
  BLUE_MAP_LAYER_NAME,
];
const MAP_GEOSPACE_LAYER_NAMES = [
  GEOSPACE_ELECTRONIC_MAP_LAYER_NAME,
  GEOSPACE_CHIBAN_TILE_LAYER_NAME,
  LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME,
];

const ZENRIN_NAMEPLATE_MAP_TYPE = "ZpcFaeMB";

// ZENRIN APIの仕様ではmaxが8000だが、一定のズームレベルを超えると8000以下でもエラーとなるため、4000に設定
// 縦横比率はA3の横向きに準拠
const ZENRIN_A3_WIDTH_PX = 4000;
const ZENRIN_A3_HEIGHT_PX = 2830;

/**
 * レイヤー選択状態と契約情報に基づいて、現在の地図モード（zenrin/geospace/null）を判定する。
 * Zenrin API と GeoSpace API の使い分けの基準となる。
 */
const detectMapMode = (
  layerNames: string[],
  hasZenrinContract: boolean,
  hasGeospaceContract: boolean
): MapMode => {
  const zenrinSelected = MAP_ZENRIN_LAYER_NAMES.some((name) =>
    layerNames.includes(name)
  );
  const geospaceSelected = MAP_GEOSPACE_LAYER_NAMES.some((name) =>
    layerNames.includes(name)
  );

  if (zenrinSelected) return "zenrin";
  if (geospaceSelected) return "geospace";

  if (hasZenrinContract || hasGeospaceContract) {
    if (hasZenrinContract) return "zenrin";
    if (hasGeospaceContract) return "geospace";
  }

  return null;
};

export const ChibanViewer: React.FC<ChibanViewerProps> = ({
  initialLatLng,
  savedMapPlotData,
  openSavedPlotModal: openSavedPlotModalFromProps,
  plotDetailsToMap: plotDetailsToMapFromProps,
  datasetInfo: datasetInfoFromProps,
  hasMapViewingPermission: hasMapViewingPermissionFromProps,
  hasZenrinContract: hasZenrinContractFromProps,
  hasGeospaceContract: hasGeospaceContractFromProps,
}) => {
  const [map, setMap] = useState<Map | null>(null);

  // レイヤーを外部から制御するためのRef
  const overlayControllerRef = useRef<OverlayController | null>(null);

  // 資料取得モーダルに渡す地番リストのState
  const [
    realEstateDocumentFetchModalChibanList,
    setRealEstateDocumentFetchModalChibanList,
  ] = useState<RealEstateDocumentChibanKaokuNumberRow[]>([]);
  const [
    realEstateDocumentFetchModalKaokuNumberList,
    setRealEstateDocumentFetchModalKaokuNumberList,
  ] = useState<RealEstateDocumentChibanKaokuNumberRow[]>([]);
  // ピン履歴情報を保存するAPI
  const createPinHistoryAPI = CreatePinHistoryAPI();
  // 住居表示から座標を取得するAPI
  const getLatLngByAddressAPI = GetLatLngByAddressAPI();
  const userConfig = UserConfigGetAPI();
  const mapChibanIsMatch = userConfig?.mapChibanIsMatch ?? false;
  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();
  // マップ上にプロットされた物件リスト（MapPlotRequest）の詳細情報を取得するための関数を取得
  const { fetchPlotDetails } = useMapPlotRequest(false);

  // UI左側テキスト入力エリアのState
  const [normalAddressTextBox, setNormalAddressTextBox] = useState<string>("");
  const [chibanAddressTextBox, setChibanAddressTextBox] = useState<string>("");

  // 地図に表示するポリゴンのデータ
  // 地図クリック、住居表示検索、地番検索でピンを立てた地点のポリゴンデータ
  const [polygonData, setPolygonData] = useState<GeoSpacePolygonCoordinates>(
    []
  );
  // 登記図面取得物件選択テーブルのポリゴンデータ
  const [selectedPropertiesPolygonData, setSelectedPropertiesPolygonData] =
    useState<GeoSpacePolygonItem[][][]>([]);
  // 物件一覧の物件を示す青ピンの位置
  const [selectedPropertiesPinData, setSelectedPropertiesPinData] = useState<
    LatLng[]
  >([]);

  // 地図の初期表示座標
  const [viewLatLng, setViewLatLng] = useState<LatLng>(initialLatLng);

  // 地図上でクリックされた座標、クリックされた時に更新される
  const [clickedLatLng, setClickedLatLng] = useState<LatLng | null>(null);
  // 物件一覧の行を押して地番を引く時の、その地点と行（handleFocusPropertyRow）。
  // 引けた筆がその行の物件の時だけポップアップを出し、別の筆なら一覧に足さない
  const rowFocusLookupRef = useRef<{ latlng: LatLng; rowId: number } | null>(
    null
  );

  // 地図に表示するポップアップのデータをまとめているState
  const [markerState, setMarkerState] = useState<MarkerState>({
    latlng: initialLatLng,
    hide: true,
    content: null,
  });

  // ズームレベルを管理するState
  const [zoom, setZoom] = useState<number>(INITIAL_ZOOM);
  const [zenrinImageSelectionMode, setZenrinImageSelectionMode] =
    useState<boolean>(false);
  const [zenrinImageSelectionBounds, setZenrinImageSelectionBounds] =
    useState<LatLngBounds | null>(null);
  const [zenrinImageOverlay, setZenrinImageOverlay] = useState<{
    bounds: LatLngBounds;
    url: string;
  } | null>(null);
  const [zenrinImageLoading, setZenrinImageLoading] = useState<boolean>(false);
  const zenrinOverlayObjectUrlRef = useRef<string | null>(null);

  // 地図出力パネル用のState
  const [mapExportPanelOpen, setMapExportPanelOpen] = useState<boolean>(false);
  // 物件一覧を大きく表示するモーダル（左の列が狭くて見づらい時に使う）
  const [propertyListDialogOpen, setPropertyListDialogOpen] = useState(false);
  // SP は物件一覧が画面幅いっぱいに出るので、拡大モーダルは使わない（一括削除も一覧の上に出す）
  const isMobile = useIsMobile();
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  useEffect(() => {
    if (isMobile) setPropertyListDialogOpen(false);
  }, [isMobile]);
  // 印刷モードで「枠線を表示」がONの時に、地図上へ出力範囲枠を出すための設定
  const [printFramePreview, setPrintFramePreview] =
    useState<MapPrintOptions | null>(null);
  // Leaflet mapインスタンスへの参照
  const mapRef = useRef<L.Map | null>(null);
  const {
    handleDownloadMapImage,
    handleCreateMapImagePreview,
    handleDownloadMapPdf,
    handleApplyPrintScaleToMap,
    mapImageDownloading,
    exportProgress,
  } = useMapImageExport({
    mapRef,
    zenrinImageOverlay,
    includeLayers: true,
    includePins: true,
    includeDrawnShapes: true,
    includeCurrentLocation: true,
  });

  // 受付帳の情報を表示するポップアップのデータを保存するState
  const [receptionBookMarkerStates, setReceptionBookMarkerStates] = useState<
    MarkerState[]
  >([]);

  // 登記図面取得物件選択テーブルのState
  const [selectedProperties, setSelectedProperties] = useState<
    PropertySelectionRow[]
  >([]);

  // 依頼ボタン押下時のフラグ
  const [acquireRequestPending, setAcquireRequestPending] = useState(false);

  // 登記図面取得モーダル用のState
  const [acquireMultipleBooksRows, setAcquireMultipleBooksRows] = useState<
    AcquireMultipleBookHandoffData[]
  >([]);

  // 登記図面取得モーダル表示制御用のState
  const [acquireMultipleBooksModalIsOpen, setAcquireMultipleBooksModalIsOpen] =
    useState<boolean>(false);

  // 都道府県リストのState
  const [prefectures, setPrefectures] = useState<IPrefecture[]>([]);

  // 受付帳ピンの読み込み状態を保存するState
  const [allReceptionBookMarkerLoaded, setAllReceptionBookMarkerLoaded] =
    useState<boolean>(false);

  // 受付帳ピン一時保存用のRef
  const receptionBookMarkerRef = useRef<MarkerState[]>([]);

  // 受付帳ポップアップを外部から開くための関数
  const openReceptionBookPopupRef = useRef<(id: number) => void>();

  // 受付帳検索画面からのstate処理済みフラグ（二重実行防止用）
  const stateKeyProcessedRef = useRef(false);

  // 家屋番号検索モーダル表示制御用のState
  const [kaokuNumberSearchModalIsOpen, setKaokuNumberSearchModalIsOpen] =
    useState<boolean>(false);

  // ゼンリン地図取得履歴モーダル表示制御用のState
  const [zenrinRequestHistoryModalIsOpen, setZenrinRequestHistoryModalIsOpen] =
    useState<boolean>(false);

  // 家屋番号検索モーダルに渡す地番リストのState
  const [kaokuNumberSearchChibanList, setKaokuNumberSearchChibanList] =
    useState<ChibanInput[]>([]);
  // 家屋番号検索の検索元の座標。結果を物件リストに足す時に、結果の行に持たせる
  const kaokuSearchSourcesRef = useRef<KaokuSearchSource[]>([]);

  // 資料取得モーダル表示制御用のState
  const [
    realEstateDocumentFetchModalIsOpen,
    setRealEstateDocumentFetchModalIsOpen,
  ] = useState<boolean>(false);

  // 資料取得モーダルに渡す住所のState
  const [
    realEstateDocumentFetchModalAddress,
    setRealEstateDocumentFetchModalAddress,
  ] = useState<string>("");

  // 資料取得モーダルに渡す地番のState
  const [
    realEstateDocumentFetchModalChiban,
    setRealEstateDocumentFetchModalChiban,
  ] = useState<string>("");

  // 資料取得モーダルに渡す所在のState
  const [
    realEstateDocumentFetchModalLocation,
    setRealEstateDocumentFetchModalLocation,
    // 役所資料取得依頼ボタン押下時のフラグ
  ] = useState<string>("");

  // 「ピンを呼び出す」モーダルの選択テーブルのState
  const [properties, setProperties] = useState<RestorePropertyRow[]>([]);

  // 「ピンを取り出す」モーダルの選択テーブルのid計算用State。
  // TODO: ピン履歴のデータ保存の仕方によっては不要となる？
  const [propertyRowCount, setPropertyRowCount] = useState<number>(0);

  // 「保存された地図プロット情報を呼び出す」モーダル表示のState
  const [SavedPlotModalOpen, setSavedPlotModalOpen] = useState<boolean>(false);

  // 「ピンを保存する」(CSV保存)モーダル表示のState
  const [csvUploadOpen, setCsvUploadOpen] = useState<boolean>(false);

  // 「取引価格の一覧を表示」モーダル表示のState
  const [tradePriceListModalOpen, setTradePriceListModalOpen] =
    useState<boolean>(false);
  const [tradePriceListRows, setTradePriceListRows] = useState<
    TradePriceGridRow[]
  >([]);
  const [tradePriceListLoading, setTradePriceListLoading] =
    useState<boolean>(false);
  const [tradePriceListLocation, setTradePriceListLocation] = useState<{
    municipality: string;
    districtName: string;
  } | null>(null);
  const [
    tradePriceListShowSearchCondition,
    setTradePriceListShowSearchCondition,
  ] = useState<boolean>(true);

  // 取引価格タイルフィルター設定のState（地図タイル表示用）
  const [tradePriceTileFilter, setTradePriceTileFilter] =
    useState<TradePriceTileFilter>(DEFAULT_TRADE_PRICE_TILE_FILTER);

  // 取引価格一覧フィルター設定のState（一覧表示・検索用）
  const [tradePriceFilter, setTradePriceFilter] =
    useState<TradePriceListFilter>(() => ({
      ...getDefaultTradePriceFilter(),
      prefecture: null,
    }));

  // 画面遷移時のpropsに基づいて初期処理を実行（プロットデータとモーダルの統合管理）
  useEffect(() => {
    // ケース1: 「保存した物件リスト一覧」モーダルの自動オープン
    // プロット詳細画面から「戻る」で遷移した場合など
    if (openSavedPlotModalFromProps) {
      setSavedPlotModalOpen(true);
    }

    // ケース2: プロット詳細画面から選択されたプロット群で遷移した場合
    if (plotDetailsToMapFromProps && Array.isArray(plotDetailsToMapFromProps)) {
      // popup導線で親 request ID を使えるよう、plotRequestId を保持する
      const plotDetails: SavedMapPlotRequestDetail[] =
        plotDetailsToMapFromProps.map(
          ({ plotRequestId, ...rest }): SavedMapPlotRequestDetail => ({
            ...rest,
            mapPlotRequestId: rest.mapPlotRequestId ?? plotRequestId,
          })
        );
      setDisplayPlotData(plotDetails);

      if (datasetInfoFromProps && Array.isArray(datasetInfoFromProps)) {
        setLoadedDatasets(appendLoadedDatasets([], datasetInfoFromProps));
      }
      return;
    }

    // ケース3: 地図プロット詳細画面の座標マーカーから遷移した場合
    // propsで直接渡されたsavedMapPlotDataを優先
    if (savedMapPlotData) {
      const { plotRequestId, ...plotDataWithoutRequestId } = savedMapPlotData;
      setDisplayPlotData([plotDataWithoutRequestId]);
    }
  }, [
    openSavedPlotModalFromProps,
    plotDetailsToMapFromProps,
    datasetInfoFromProps,
    savedMapPlotData,
  ]);

  /**
   * 地図上に表示するプロットデータの状態管理
   *
   * 以下の3つのデータソースから受け取ったプロットデータを統合管理します：
   * 1. プロット詳細画面でピンを反映した場合 (props.plotDetailsToMapFromProps)
   * 2. 地図プロット詳細画面の座標マーカーをクリックして遷移した場合 (props.savedMapPlotData)
   * 3. 「ピンを呼び出す」モーダルで選択された場合 (SavedPlotModalのコールバック)
   *
   * 優先順位: 1 > 2 > 3
   */
  const [displayPlotData, setDisplayPlotData] = useState<
    SavedMapPlotRequestDetail[]
  >([]);

  // 読み込まれたMapPlotRequestの識別名とピンの色を保持するState
  const [loadedDatasets, setLoadedDatasets] = useState<LoadedDataset[]>([]);
  const savedPlotColorByRequestId = useMemo(
    () => toColorByRequestId(loadedDatasets),
    [loadedDatasets]
  );

  /** 表示中のプロットデータから1つのリストを地図から外す（保存した物件リスト自体は消さない） */
  const handleRemoveDataset = (id: number): void => {
    const next = removeLoadedDataset(
      { datasets: loadedDatasets, plots: displayPlotData },
      id
    );
    setLoadedDatasets(next.datasets);
    setDisplayPlotData(next.plots);
  };

  // 不動産検索ドロワーの展開状態
  const [realEstateSearchDrawerOpen, setRealEstateSearchDrawerOpen] =
    useState(false);

  // 不動産検索の結果
  const [realEstateSearchResult, setRealEstateSearchResult] = useState<
    RealEstateSearchResult[]
  >([]);

  // ポップアップタグ関連の機能をカスタムフックで管理
  const {
    popupInfo: currentPopupIdentifier,
    setPopupInfo: setCurrentPopupIdentifier,
    setCurrentPopupRowId,
    handleTagsChange,
    handleBluePinTagsChange,
    handleRequestedPicturesTagsChange,
    handleRequestedPicturesRemarksChange,
    handleRequestedPicturesAdditionalFieldsChange,
    handleMapClickRemarksChange,
    handleMapClickAdditionalFieldsChange,
    handleRealEstateSearchRemarksChange,
    handleRealEstateSearchAdditionalFieldsChange,
    handleReceptionBookTagsChange,
    handleReceptionBookRemarksChange,
    handleReceptionBookAdditionalFieldsChange,
    handleMapClickTagsUpdate,
    handleMapClickRemarksUpdate,
    handleMapClickAdditionalFieldsUpdate,
    resetPopupTags,
  } = usePopupTagHandlers({
    selectedProperties,
    setSelectedProperties,
    realEstateSearchResult,
    setRealEstateSearchResult,
  });

  // 前回のselectedProperties.lengthを保持するref
  const prevSelectedPropertiesLengthRef = useRef<number>(
    selectedProperties.length
  );

  // selectedPropertiesが0以上から0に変化した時
  // または表示中の地図クリックピンに対応する行が削除された時に青ピンを削除する(ただし住所が取れず、不明な青ピンは除く）
  useEffect(() => {
    const prevLength = prevSelectedPropertiesLengthRef.current;
    const currentSelectedPropertiesLength = selectedProperties.length;

    // ここでcurrentPopupIdentifierは現在表示している青ピンを指している
    // 青ピンの情報とselectedPropertiesの配列を比較して一致があれば、青ピンは削除しない
    // 一致なしの場合は青ピンに紐づくselectedPropertiesは削除されたとみなして、青ピンを削除する
    // currentPopupIdentifier自体が存在しない場合(地番が取れなかった場合）は青ピンはそのままとします
    // selectedPropertiesと青ピンが一致しているかの判定は下記条件文を見てください
    const hasLinkWithBluePinAndSelectedProperties = currentPopupIdentifier
      ? selectedProperties.some((row) =>
          isRowOfMapClickPin(row, currentPopupIdentifier)
        )
      : true;

    // 青ピンを削除するかどうかの判定
    // リストが空になった、もしくは現在表示している青ピンの行が削除された（ただし、住所が取れず不明な青ピンは除く）
    const shouldClearBluePin =
      (prevLength > 0 && currentSelectedPropertiesLength === 0) ||
      (currentPopupIdentifier && !hasLinkWithBluePinAndSelectedProperties);

    if (shouldClearBluePin) {
      setMarkerState({
        latlng: null,
        hide: true,
        content: null,
      });
      setClickedLatLng(null);
      setPolygonData([]);
      setCurrentPopupIdentifier(null);
      setCurrentPopupRowId(null);
    }

    // 現在の長さを保存
    prevSelectedPropertiesLengthRef.current = currentSelectedPropertiesLength;
  }, [
    selectedProperties,
    currentPopupIdentifier,
    setClickedLatLng,
    setMarkerState,
    setPolygonData,
    setCurrentPopupIdentifier,
    setCurrentPopupRowId,
  ]);

  // 不動産検索結果のボタンの制御に使用する
  const [openRealEstateSearchResultList, setOpenRealEstateSearchResultList] =
    useState<null | HTMLElement>(null);
  const handleClickRealEstateSearchResultButton = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setOpenRealEstateSearchResultList(event.currentTarget);
  };
  const handleCloseRealEstateSearchResultButton = (): void => {
    setOpenRealEstateSearchResultList(null);
  };

  // 物件概要書の新規作成モーダルのState
  const [newPropertySummeryModalIsOpen, setNewPropertySummeryModalIsOpen] =
    useState(false);

  // チェックされたレイヤーのState
  // Propsから受け取った契約情報を使用（LocalStorageからは読み取らない）
  // これにより、親コンポーネントから渡された契約情報を確実に反映できる
  const hasZenrinContract: boolean = hasZenrinContractFromProps;
  const hasGeospaceContract: boolean = hasGeospaceContractFromProps;

  // 初期 checkedLayers は mount 時に一度だけ算出する（遅延初期化）。
  // 契約 props 変化後の追従は下の同期 useEffect が担うため、ここで再計算する必要はない。
  const [checkedLayers, setCheckedLayers] = useState<string[]>(() =>
    computeUrlBasedCheckedLayers(hasZenrinContract, hasGeospaceContract, [])
  );

  // props の契約情報が変更された場合に checkedLayers を同期する。
  // 関数型更新（prev ベース）で算出することで deps に checkedLayers を含めずに最新値を参照でき、
  // stale closure を避けつつ、変更不要時は prev を同一参照で返して不要な再レンダーを抑制する。
  //
  // 不変条件: 現アーキでは親 ChibanViewerAccessControl が契約確定後にのみ ChibanViewer を
  // マウントするため、マウント後に契約 props は変化せず、この同期は実質発火しない（防御的実装）。
  // マウントゲートが崩れて契約が後追い確定する設計になった場合にのみ意味を持つ。
  useEffect(() => {
    setCheckedLayers((prev) => {
      // 現在の checkedLayers から契約系初期レイヤー以外の「extra」を抜き出し、
      // BASE 部分が初期状態のままなら契約用レイヤーへ拡張する（extra は保持し URL パラメータも再反映）。
      // prev を 1 回だけ走査し、BASE 系の件数と extra を同時に集計する。
      let baseCount = 0;
      const extraLayers: string[] = [];
      for (const layer of prev) {
        if (BASE_INITIAL_LAYERS.includes(layer)) {
          baseCount += 1;
        } else {
          extraLayers.push(layer);
        }
      }
      const isDefaultBase =
        // BASE 系は BASE_INITIAL_LAYERS のみで構成されるため、件数一致で初期状態とみなせる
        baseCount === BASE_INITIAL_LAYERS.length &&
        // ゼンリン/GEOSPACE 初期レイヤーが extra に含まれていない（= まだ契約拡張前）
        !extraLayers.some(
          (layer) =>
            ZENRIN_INITIAL_LAYERS.includes(layer) ||
            GEOSPACE_INITIAL_LAYERS.includes(layer)
        );

      // 拡張不要（初期状態でない or 契約なし）なら prev を同一参照で返す
      if (!isDefaultBase || (!hasZenrinContract && !hasGeospaceContract)) {
        return prev;
      }
      return computeUrlBasedCheckedLayers(
        hasZenrinContract,
        hasGeospaceContract,
        extraLayers
      );
    });
  }, [hasZenrinContract, hasGeospaceContract]);

  const isZenrinLayerActive = useMemo(
    () => MAP_ZENRIN_LAYER_NAMES.some((layer) => checkedLayers.includes(layer)),
    [checkedLayers]
  );
  const isTradePriceLayerActive = useMemo(
    () => checkedLayers.includes(TRADE_PRICE_LAYER_NAME),
    [checkedLayers]
  );
  // mapMode を導出値として計算（常に最新の値が保証される）
  // これによりレイヤー変更とAPI呼び出しの競合状態を防ぐ
  const mapMode = useMemo(
    () => detectMapMode(checkedLayers, hasZenrinContract, hasGeospaceContract),
    [checkedLayers, hasZenrinContract, hasGeospaceContract]
  );

  // custom hooks 呼び出し
  const { apiClient } = useApiClient();
  const { reassistArchive } = useFeatureFlags();
  // 住居表示の検索で住所が見つからないとき、マンション名などで役所資料（黒塗り）の物件を探す
  const { findAndShow: showSaleDocumentByKeyword, showMunicipalDocumentLayer } =
    useSaleDocumentSearchFallback({
      apiClient,
      setViewLatLng,
      setCheckedLayers,
      // reassist-archive フラグがオフなら、役所資料（黒塗り）は探さず、レイヤーもオンにしない
      enabled: reassistArchive === true,
      // 地図側の表示状態にも反映する（checkedLayers の更新だけでは、地図のレイヤーが切り替わらない）
      setOverlayVisible: (name, visible) => {
        overlayControllerRef.current?.setOverlay(name, visible);
      },
    });
  const { handleSubmit: acquireBookFormHandleSubmit } = useAcquireBookForm();

  const [csvUploadLoading, setCsvUploadLoading] = useState<boolean>(false);
  const [mapPlotImportOpen, setMapPlotImportOpen] = useState<boolean>(false);
  const [openedFromPinSave, setOpenedFromPinSave] = useState<boolean>(false);

  const {
    kaokuNumberSearch: kaokuNumberSearchFlag,
    realEstateDocumentFetch: realEstateDocumentFetchFlag,
    mapPropertyListHistory,
    mapPlotCsvImport,
    mapRealestateSearch,
    crm,
    tradePriceSearch,
    trustartChibanMap,
    registrationMapLayer,
  } = useFeatureFlags();

  /**
   * savePinHistory 受付帳情報を保存する
   * @param selectedItem 受付帳IDのリスト
   */
  const savePinHistory = async (
    selectedItem: AcquireMultipleBookHandoffData[]
  ): Promise<CreatePinHistoryResponse | undefined> => {
    const bookIDs = selectedItem
      .map((value) => value.bookId)
      .filter((id): id is number => id !== undefined);
    const response = await createPinHistoryAPI.trigger(bookIDs);
    if (response) {
      return response;
    } else {
      console.error("savePinHistory error");
      return response;
    }
  };

  // 受付帳ピン表示時の地図モード解決処理（受付帳投影フロー専用）。
  // 受付帳投影では地番検索のカバー率が高い geospace を優先する。
  // 地図初期表示が zenrin（mapMode === "zenrin"）でも、geospace 契約があれば
  // geospace で座標解決を行い、zenrin は geospace 契約が無い場合のフォールバックとして使う。
  // ※この優先順位は受付帳投影フロー専用。通常の地図検索（handleChangeSearchTerm 等）は
  //   従来どおり mapMode をそのまま使用するため影響しない。
  // 座標解決（locationSearch）と失敗ログ（modeForLog）の双方から参照するため、
  // 優先順位ロジックの二重管理を避けてここで一元化する。
  const resolveMapModeForReceptionBook = useCallback((): MapMode => {
    if (hasGeospaceContract) return "geospace";
    if (hasZenrinContract) return "zenrin";
    if (mapMode) return mapMode;
    return null;
  }, [hasGeospaceContract, hasZenrinContract, mapMode]);

  /**
   * getAllPopupReceptionBooks
   * feed画面から受け取った配列から、ポップアップに表示するIPopupBookContent型の配列にして返す
   * selectedItemに含まれるbookIdから受付帳の詳細をバックエンドから取得する
   * prefName,locationFull,chibanKaokuNumberを用いて住居表示をバックエンドから取得する
   *
   * return: getPopupBookResult[] API呼び出しクエリとその結果のリスト
   */
  const getAllPopupReceptionBooks = async (
    selectedItem: AcquireMultipleBookHandoffData[],
    onSuccess: (content: IPopupBookContent) => void,
    onFail: (
      item: AcquireMultipleBookHandoffData,
      searchResult: GeoSpaceChiban | undefined,
      bookResult: ReceptionBook | undefined
    ) => void
  ): Promise<{
    results: GetPopupBookResult[];
    hadZenrinRateLimit: boolean;
  }> => {
    // 地番検索は完全一致で行う
    let hadZenrinRateLimit = false;
    // 429 後に残件のゼンリン検索を止め、BE 超過ログの大量化を防ぐ
    let abortZenrinSearch = false;
    // 並列に送るため、429 の待機は全件で共有する（1件が待つ間に他の件が送り続けて連続で 429 になるのを防ぐ）
    let resumeAt = 0;
    const waitForResume = async (): Promise<void> => {
      const waitMs = resumeAt - Date.now();
      if (waitMs > 0) {
        await sleepMs(waitMs);
      }
    };

    // ゼンリンは受付帳プロット専用の API を使う。検索語は BE が受付帳の行から組み立てる
    const searchZenrinChiban = async (
      bookId: number
    ): Promise<GeoSpaceChiban | undefined> => {
      await waitForResume();
      const zenrinResult = await zenrinSearchByReceptionBook(bookId);
      return zenrinResult
        ? zenrinToGeoSpaceChiban(zenrinResult) ?? undefined
        : undefined;
    };

    const locationSearch = async (
      item: AcquireMultipleBookHandoffData
    ): Promise<GeoSpaceChiban | undefined> => {
      const query = `${item.prefName}${item.locationFull}${item.chibanKaokuNumber}`;
      const mode = resolveMapModeForReceptionBook();
      if (mode === "zenrin") {
        const { bookId } = item;
        if (abortZenrinSearch || bookId === undefined) {
          return undefined;
        }
        try {
          return await searchZenrinChiban(bookId);
        } catch (error) {
          if (!isZenrinRateLimitError(error)) {
            throw error;
          }
          hadZenrinRateLimit = true;

          // RPS の短い窓なら 1 回だけ待って再試行。RPM など長い窓は待たず打ち切る。
          if (shouldWaitAndRetryZenrinRateLimit(error.retryAfterSeconds)) {
            resumeAt = Math.max(
              resumeAt,
              Date.now() + (error.retryAfterSeconds ?? 1) * 1000
            );
            try {
              return await searchZenrinChiban(bookId);
            } catch (retryError) {
              if (!isZenrinRateLimitError(retryError)) {
                throw retryError;
              }
              showZenrinRateLimitToast(retryError);
              abortZenrinSearch = true;
              return undefined;
            }
          }

          showZenrinRateLimitToast(error);
          abortZenrinSearch = true;
          return undefined;
        }
      }

      if (mode === "geospace") {
        const geospaceResult = await geospaceSearch(query, true);
        if (geospaceResult) return geospaceResult;
        return await geospaceLegalAffairsSearch(query, true);
      }

      return undefined;
    };

    const toSkippedResult = (
      item: AcquireMultipleBookHandoffData
    ): GetPopupBookResult => {
      const query = `${item.prefName}${item.locationFull}${item.chibanKaokuNumber}`;
      return {
        query: { book_id: item.id, address: query },
        search: undefined,
        book: undefined,
      };
    };

    const processItem = async (
      item: AcquireMultipleBookHandoffData
    ): Promise<GetPopupBookResult> => {
      const query = `${item.prefName}${item.locationFull}${item.chibanKaokuNumber}`;
      // 住居表示と緯度経度を取得
      const search = locationSearch(item);
      // バックエンドAPI呼び出し、ここで受付帳データを持ってくる
      if (item.bookId === undefined) {
        onFail(item, undefined, undefined);
        return {
          query: { book_id: item.id, address: query },
          search: undefined,
          book: undefined,
        };
      }
      const book = getReceptionBook(item.bookId);

      // search,bookのAPIレスポンスを待つ
      const [searchResult, bookResult] = await Promise.all([search, book]);

      if (searchResult && bookResult) {
        // 住居表示、緯度経度、受付帳データ全て揃った
        const jyuukyoHyouji = `${searchResult.prefName}${
          searchResult.cityName
        }${searchResult.ooazaName ?? ""}${searchResult.azaName ?? ""}${
          searchResult.chiban
        }`;
        onSuccess({
          lat: searchResult.lat,
          lng: searchResult.lng,
          book: bookResult,
          jyuukyoHyouji,
          _id: item.id,
          chibanRawData: searchResult,
        });
      } else if (searchResult) {
        // 住居表示と、緯度経度は揃ったが受付帳データの取得に失敗した
        const jyuukyoHyouji = `${searchResult.prefName}${
          searchResult.cityName
        }${searchResult.ooazaName ?? ""}${searchResult.azaName ?? ""}${
          searchResult.chiban
        }`;
        onSuccess({
          lat: searchResult.lat,
          lng: searchResult.lng,
          jyuukyoHyouji,
          _id: item.id,
          chibanRawData: searchResult,
        });
      } else {
        onFail(item, searchResult, bookResult);
      }
      // API呼び出し時のクエリと呼び出し結果をまとめて返す
      return {
        query: { book_id: item.id, address: query },
        search: searchResult,
        book: bookResult,
      };
    };

    // ゼンリンは利用者単位レート制限があるため、同時に送る件数を絞る。
    // （全件 Promise.all だと BE の 1 秒あたりの枠や、ゼンリン側の秒間上限に当たりうる）
    // 429 後は残件の検索を打ち切る（短い retry_after のみ全件で待って 1 回再試行）。
    // geospace は対象外なので従来どおり並列でよい。
    const mode = resolveMapModeForReceptionBook();
    if (mode !== "zenrin") {
      const results = await Promise.all(selectedItem.map(processItem));
      return { results, hadZenrinRateLimit };
    }

    const results = await runWithConcurrency(
      selectedItem,
      ZENRIN_RECEPTION_BOOK_PLOT_CONCURRENCY,
      // レート制限打ち切り後は API を呼ばずスキップ（onFail/console も出さない）
      async (item) =>
        abortZenrinSearch ? toSkippedResult(item) : await processItem(item)
    );

    return { results, hadZenrinRateLimit };
  };

  // 地図表示権限の判定（AccessControlから渡された値を使用）
  // useMapServicePermissionフックを削除し、propsから直接権限情報を取得するように変更
  // これにより、権限管理が親コンポーネントに集約され、一貫性が保たれる
  const hasMapViewingPermission = hasMapViewingPermissionFromProps;

  // 役所地番地図を利用できるユーザーか（BE の kill-switch 無効中は全ユーザー、有効化後は契約テナントのみ）
  const currentUser = useCurrentUser();
  const isMunicipalParcelMapAvailable =
    currentUser?.isMunicipalParcelMapAvailable ?? false;

  const isTrustartLayerChecked = checkedLayers.includes(
    TRUSTART_CHIBAN_LAYER_NAME
  );
  const isRegistrationMapLayerChecked = checkedLayers.includes(
    REGISTRATION_MAP_LAYER_NAME
  );
  // ゼンリンの「表示中」は地番相当のブルーマップで判定する（住宅地図は背景のため含めない）。
  // GEOSPACE が地番タイルの ON/OFF で判定しているのと揃える。
  const isBlueMapLayerChecked = checkedLayers.includes(BLUE_MAP_LAYER_NAME);

  // 各データソースの「使えるか（available）」と「表示中か（visible）」。
  // 叩く条件の判定は buildSearchSourceStates に集約している。
  const searchSourceStates = useMemo(
    () =>
      buildSearchSourceStates({
        hasGeospaceContract,
        hasZenrinContract,
        trustartChibanMap: Boolean(trustartChibanMap),
        isMunicipalParcelMapAvailable,
        registrationMapLayer: Boolean(registrationMapLayer),
        visible: {
          geospaceChiban: checkedLayers.includes(
            GEOSPACE_CHIBAN_TILE_LAYER_NAME
          ),
          geospaceLegalAffairs: checkedLayers.includes(
            LEGAL_AFFAIRS_CHIBAN_TILE_LAYER_NAME
          ),
          trustartChiban: isTrustartLayerChecked,
          registrationMap: isRegistrationMapLayerChecked,
          zenrin: isBlueMapLayerChecked,
        },
      }),
    [
      hasGeospaceContract,
      hasZenrinContract,
      checkedLayers,
      trustartChibanMap,
      isMunicipalParcelMapAvailable,
      registrationMapLayer,
      isTrustartLayerChecked,
      isRegistrationMapLayerChecked,
      isBlueMapLayerChecked,
    ]
  );

  // クリック・登記地番検索・住居表示検索・範囲選択検索が共通で消費する検索チェーン。
  // 「どのソースを、どの順で叩くか」の判断はすべてここに集約する。
  const searchChain = useMemo(
    () => buildSearchChain({ mapFamily: mapMode, sources: searchSourceStates }),
    [mapMode, searchSourceStates]
  );
  // 地番検索（登記地番・住居表示の逆引き）を使えるか。クリック検索と同じく、引ける
  // データソースが 1 つでもあれば使える（表示レイヤーの ON/OFF は順番にしか効かない）。
  const canUseParcelSearch = searchChain.length > 0;

  const { buildLatLngLookup, buildChibanLookup, buildAddressLookup } =
    useParcelSearchSources();

  /**
   * 非表示のソース（Tier 2）でヒットしたときだけ、乖離を伝える通知を出す。
   *
   * 通知のボタンから該当レイヤーを表示し、そのレイヤーが描画され始めるズームまで
   * 寄せることで、「見ている地図」と「結果の出どころ」のずれをその場で解消できる。
   * ボタンはレイヤーを確実に切り替えられるとき（パネルで操作でき、タイルも描画できる）
   * だけ出す。表示中のソース（Tier 1）でのヒットはずれが無いので何も出さない。
   */
  const notifyIfSourceMismatch = useCallback(
    (resolution: ChainResolution): void => {
      if (resolution.kind !== "hit" || resolution.tier !== 2) return;

      const { layerName, minZoom } = SEARCH_SOURCE_LAYER[resolution.sourceId];
      const controller = overlayControllerRef.current;
      notifySourceMismatch({
        sourceId: resolution.sourceId,
        onShowLayer: controller?.canShowOverlay(layerName)
          ? () => {
              controller.setOverlay(layerName, true);
              if (map && map.getZoom() < minZoom) {
                map.setZoom(minZoom);
              }
            }
          : undefined,
      });
    },
    [map]
  );

  // 地番検索そのものは useParcelSearchSources 側に移っているため、ここで直接使うのは
  // 受付帳投影・無料プランの住所検索・ゼンリン画像取得など、チェーン外の用途だけ。
  const { geospaceSearch, geospaceLegalAffairsSearch, geospaceReverseFree } =
    useGeoSpaceApi();
  const {
    zenrinSearchByReceptionBook,
    zenrinRequestMapImage,
    zenrinGetMapImage,
  } = useZenrinApi();
  const { getReceptionBook } = useReceptionBookApi();

  useEffect(() => {
    (async () => {
      // 都道府県リストをAPIから取得する
      const prefectures = await GetPrefecturesAPI(apiClient);
      if (prefectures.length !== 0) {
        setPrefectures(prefectures);
      }
    })();
  }, []);

  useEffect(() => {
    // mapModeが確定していない場合は待機
    // (契約があるのにmapModeがnullの場合は、checkedLayersの更新を待つ)
    if (mapMode === null && (hasZenrinContract || hasGeospaceContract)) {
      return;
    }

    // 既に処理済みの場合はスキップ
    if (stateKeyProcessedRef.current) {
      return;
    }

    (async () => {
      // URLからstateKeyを取得
      const urlParams = new URLSearchParams(window.location.search);
      const stateKey = urlParams.get("stateKey");
      if (!stateKey) {
        return;
      }

      // 処理開始をマーク（localStorageを読む前にマーク）
      stateKeyProcessedRef.current = true;

      const state = JSON.parse(
        localStorage.getItem(stateKey) ?? "{}"
      ) as LocationStateWithHandOff;

      // 不動産種別が土地以外のデータが含まれている場合、エラーメッセージを表示
      const argument =
        (state?.handOffMessage?.argument as AcquireMultipleBookHandoffData[]) ??
        [];
      if (argument.some((item) => item.bookType !== "LAND")) {
        toast.error("不動産種別が土地以外のデータは地図に反映されません");
      }

      localStorage.removeItem(stateKey); // 使用後に削除
      // handOffMessageが存在するか
      if (state?.handOffMessage) {
        const handOffMessage = state.handOffMessage;
        // handOffMessageのcommandが"MAP_DRAW_RECEPTION_BOOKS"であるか
        if (handOffMessage.command === "MAP_DRAW_RECEPTION_BOOKS") {
          // handOffMessageから選択した受付帳の"土地"データのみを取り出す
          const selectedItem = (
            (handOffMessage?.argument as AcquireMultipleBookHandoffData[]) ?? []
          ).filter((item) => item.bookType === "LAND");

          // 保存API呼び出し
          void savePinHistory(selectedItem);

          // 受付帳投影は geospace 座標で行う（resolveMapModeForReceptionBook が geospace 優先）。
          // 地図がゼンリン表示のままだと、ゼンリン住宅地図/ブルーマップは位置精度が
          // geospace の地番座標と一致しないため、ピンが地番からズレて見える。
          // そこで geospace 契約があり、かつ現在ゼンリン表示中の場合に限り、
          // 地図レイヤーも geospace（地番タイル）へ切り替えてズレを防ぐ。
          // setOverlay 内の排他制御（enforceExclusiveOverlayVisibility）により
          // ゼンリン系レイヤーは自動的に OFF になる。
          if (
            selectedItem.length > 0 &&
            hasGeospaceContract &&
            mapMode === "zenrin"
          ) {
            overlayControllerRef.current?.setOverlay(
              GEOSPACE_CHIBAN_TILE_LAYER_NAME,
              true
            );
          }

          // 全データ取得
          const totalCount = selectedItem.length;
          let successCount = 0;

          const { hadZenrinRateLimit } = await getAllPopupReceptionBooks(
            selectedItem,
            (content) => {
              // データ取得成功時の処理
              successCount++;
              const marker: MarkerState = {
                latlng: new LatLng(content.lat, content.lng),
                _id: content._id,
                book: content.book,
                chibanRawData: content.chibanRawData,
              };
              receptionBookMarkerRef.current = [
                marker,
                ...receptionBookMarkerRef.current,
              ];
              setReceptionBookMarkerStates(receptionBookMarkerRef.current);
            },
            (item, searchResult, bookResult) => {
              // データ取得失敗時の処理
              // 座標が取得できない場合、ピンを立てることができない
              // 投影で使用した地図モードをログ出力する（共通の resolveMapModeForReceptionBook を参照）
              const modeForLog = resolveMapModeForReceptionBook() ?? "none";
              if (searchResult === undefined) {
                const query = `${item.prefName}${item.locationFull}${item.chibanKaokuNumber}`;
                console.error(
                  `API失敗 locationSearch(${modeForLog}): ${query} returned undefined`
                );
              }
              if (bookResult === undefined) {
                console.error(
                  `API失敗 getReceptionBook(${
                    item.bookId ?? "undefined"
                  }) returned undefined`
                );
              }
            }
          );

          // 投影結果をtoastで表示
          // 429 のときは赤トースト済みなので、「投影できる土地データがありませんでした」は出さない
          if (successCount === 0) {
            if (!hadZenrinRateLimit) {
              toast.warning(`投影できる土地データがありませんでした`);
            }
          } else if (successCount === totalCount) {
            toast.success(
              `${successCount.toLocaleString(
                "ja-JP"
              )}件の土地データを投影しました`
            );
          } else {
            toast.warning(
              `${totalCount.toLocaleString(
                "ja-JP"
              )}件中${successCount.toLocaleString(
                "ja-JP"
              )}件の土地データを投影しました`
            );
          }

          setAllReceptionBookMarkerLoaded(true);
          handleClickImportReceptionBookMarkers();
        }
      }
    })();
  }, [mapMode, hasZenrinContract, hasGeospaceContract]);

  /**
   * 住居表示検索で地番が取れず、座標だけのピンにフォールバックしたときに、
   * 左UIの「登記地番」テキストボックスをクリアする。
   * 前回検索でセットされた地番が残っていると、地番を持たない表示中のピンと
   * 食い違って見えてしまうため。
   * 地番検索からのフォールバックでは呼ばない（ユーザーの入力値そのものを消してしまうため）。
   */
  const clearChibanTextBoxOnFallback = (): void => {
    setChibanAddressTextBox("");
  };

  /**
   * 住所検索・地番検索が失敗した場合のフォールバック処理。
   * デフォルトでは、入力された住所の近辺へ地図を移動し、Google Maps API で得た住所をUIに反映させる。
   * 完全一致モード (`options.isMatch === true`) の場合はフォールバックを実施せず、ピンとポリゴンを非表示にする。
   * @param address 検索する住所
   * @param options.isMatch 完全一致モードかどうか
   */
  /**
   * 役所系ソース（役所地番地図・登記所備付地図）の住所→地番検索で筆ミス（not_found）
   * だった場合に、BE が返した geocode 済み座標へピンを置く（再ジオコーディングなしで
   * whenSearchFail 相当のピン表示を再現する）。
   */
  const showBackendGeocodedPin = (geocoded: GeocodedPoint): void => {
    const latlng = new LatLng(geocoded.lat, geocoded.lng);
    setViewLatLng(latlng);
    setNormalAddressTextBox(geocoded.address);
    setPolygonData([]);
    // 地番が特定できていないため空文字
    resetPopupTags("", "");
    setMarkerState({
      latlng,
      hide: false,
      content: (
        <PopupContent
          // 地番は特定できていない。住所をそのまま地番として出すと実際の地番と
          // 食い違うことがほとんどなので、地番欄はブランクにする
          addressChiban={""}
          address={geocoded.address}
          prefName={""}
          cityName={""}
          ooazaName={""}
          azaName={""}
          chiban={""}
          kaokuNumberSearchFlag={kaokuNumberSearchFlag as boolean}
          handleNewPropertySheet={handleNewPropertySheet}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag as boolean}
          area={null}
          handlePinSave={handlePinSave}
          initialTags={[]}
          onTagsChange={handleTagsChange}
          crm={crm as boolean}
          hasMapViewingPermission={!!hasMapViewingPermission}
          latlng={latlng}
          availableFields={availableFields}
          hasAvailableFields={hasAvailableFields}
          // BE の座標も whenSearchFail と同じく Google のジオコーディング結果なので同じ出典にする
          sourceLabel={GOOGLE_GEOCODE_SOURCE_LABEL}
        />
      ),
    });
  };

  const whenSearchFail = async (
    address: string,
    options?: { isMatch?: boolean }
  ): Promise<void> => {
    toast.info("地番データが存在しませんでした");

    const isMatch = options?.isMatch ?? false;

    if (isMatch) {
      setPolygonData([]);
      setMarkerState((prevState: MarkerState) => ({
        ...prevState,
        latlng: null,
        hide: true,
        content: null,
      }));
      return;
    }

    const request = { address };
    const result = await getLatLngByAddressAPI.trigger(request);

    // NOTE: 結果が見つからない場合や緯度経度が特定できない場合は何もしない
    if (result == null || isNaN(result[0]) || isNaN(result[1])) {
      return;
    }

    // 地図を移動し、住所入力欄にGoogle Map APIによって得られた住所を設定する
    const latlng = new LatLng(result[0], result[1]);
    setViewLatLng(latlng);
    setNormalAddressTextBox(result[2]);
    // Google Map APIの結果では地番情報がないため空文字
    resetPopupTags("", "");
    setMarkerState({
      latlng,
      hide: false,
      content: (
        <PopupContent
          // 地番は特定できていない。住所をそのまま地番として出すと実際の地番と
          // 食い違うことがほとんどなので、地番欄はブランクにする
          addressChiban={""}
          address={result[2]}
          prefName={""}
          cityName={""}
          ooazaName={""}
          azaName={""}
          chiban={""}
          kaokuNumberSearchFlag={kaokuNumberSearchFlag as boolean}
          handleNewPropertySheet={handleNewPropertySheet}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag as boolean}
          area={null}
          handlePinSave={handlePinSave}
          initialTags={[]}
          onTagsChange={handleTagsChange}
          crm={crm as boolean}
          hasMapViewingPermission={!!hasMapViewingPermission}
          latlng={latlng}
          availableFields={availableFields}
          hasAvailableFields={hasAvailableFields}
          sourceLabel={GOOGLE_GEOCODE_SOURCE_LABEL}
        />
      ),
    });
  };

  /**
   * 登記地番の検索処理。
   *
   * @param chibanOverride - URL パラメータ経由など、`chibanAddressTextBox` ではなく
   *   引数で地番を直接指定したい場合の値。省略時は state を参照する。
   */
  const handleChangeSearchTerm = async (
    chibanOverride?: string
  ): Promise<void> => {
    const chibanSearchTerm = chibanOverride ?? chibanAddressTextBox;
    if (chibanSearchTerm.trim() === "") {
      if (chibanOverride === undefined) {
        toast.error("登記地番を入力してください");
      }
      return;
    }
    // レースコンディション防止: 関数開始時のチェーンを保持し、await 中にユーザーが
    // レイヤーを切り替えても検索開始時の条件で一貫して処理する。
    const chain = searchChain;
    try {
      // ゼンリンが検索語を弾いた理由。他のソースも当たらなかったときだけ出す。
      let zenrinRejectedMessage: string | undefined;

      // チェーンを先頭から順に引き、最初のヒットで打ち切る。どのソースをどの順で叩くかは
      // buildSearchChain が決めており、ここは結果 kind に応じた副作用（地図移動・
      // ポップアップ・トースト・whenSearchFail）の適用だけを担う。
      const resolution = await runSearchChain({
        chain,
        lookup: buildChibanLookup(chibanSearchTerm, {
          onZenrinRejected: (message) => {
            zenrinRejectedMessage = message;
          },
        }),
        requiresAddress: REQUIRES_ADDRESS_SOURCES,
      });

      // レート制限で打ち切ったときは、Google フォールバック（whenSearchFail）も呼ばない
      if (resolution.kind === "rateLimited") {
        toast.warning(RATE_LIMITED_MESSAGE);
        return;
      }

      // ゼンリンが検索語を弾いて他も当たらなかったときは、理由のトーストだけで終える。
      // 他のソースで当たった・座標が分かったときは、その結果の表示だけにする。
      if (
        resolution.kind === "notFound" &&
        zenrinRejectedMessage !== undefined
      ) {
        toast.error(zenrinRejectedMessage);
        return;
      }

      if (resolution.kind === "hit") {
        const r = resolution.result;
        const latlng = new LatLng(r.lat, r.lng);
        // 地図の表示位置をAPI呼び出し結果の座標に移動する
        setViewLatLng(latlng);
        // API呼び出し結果のポリゴンを表示する
        setPolygonData(r.polygon?.coordinates ?? []);
        // ポップアップの状態を設定する
        const {
          existingTags,
          existingAdditionalFields,
          builtLocation,
          chiban,
        } = addOrFindMapClickProperty({
          ...mapClickPropertyParams,
          searchResult: r,
        });
        setMarkerState({
          latlng,
          hide: false,
          content: (
            <PopupContent
              addressChiban={r.addressChiban}
              address={r.address}
              prefName={r.prefName}
              cityName={r.cityName}
              ooazaName={r.ooazaName ?? ""}
              azaName={r.azaName ?? ""}
              chiban={r.chiban}
              handleKaokuNumberSearch={handleKaokuNumberSearch}
              handleRealEstateDocumentFetchModal={
                handleRealEstateDocumentFetchModal
              }
              kaokuNumberSearchFlag={kaokuNumberSearchFlag as boolean}
              realEstateDocumentFetchFlag={
                realEstateDocumentFetchFlag as boolean
              }
              area={r.area}
              handlePinSave={handlePinSave}
              initialTags={existingTags}
              onTagsChange={handleTagsChange}
              initialAdditionalFields={existingAdditionalFields}
              onAdditionalFieldsChange={(additionalFields) => {
                handleMapClickAdditionalFieldsChange(
                  additionalFields,
                  builtLocation,
                  chiban,
                  r?.prefName ?? ""
                );
              }}
              crm={crm as boolean}
              hasMapViewingPermission={!!hasMapViewingPermission}
              handleNewPropertySheet={handleNewPropertySheet}
              latlng={latlng}
              availableFields={availableFields}
              hasAvailableFields={hasAvailableFields}
              sourceLabel={SEARCH_SOURCE_LABEL[resolution.sourceId]}
            />
          ),
        });
        // 左のUIの住居表示をAPI呼び出した結果の住居表示で更新
        setNormalAddressTextBox(NYHalfWidthNumberToFillWidthNumber(r.address));
        notifyIfSourceMismatch(resolution);
      } else if (resolution.kind === "geocodedPin") {
        // 役所系が筆ミスで返した座標へピン（Google を呼ばずに済ませる）
        showBackendGeocodedPin(resolution.geocoded);
      } else if (resolution.kind === "notAvailable") {
        // 全ソースミス。非対応エリアの通知のみ（whenSearchFail の Google は呼ばない）
        toast.info(resolution.message);
      } else if (hasMapViewingPermission) {
        // 有料: 従来どおり Google ジオコーディングでピン表示
        await whenSearchFail(chibanSearchTerm, {
          isMatch: mapChibanIsMatch,
        });
      } else {
        // 無料: 有料 API（whenSearchFail の Google）を呼ばない
        toast.info("地番データが存在しませんでした");
      }
    } catch (error) {
      // ゼンリンのレート制限(429)は「データなし」ではないため、Google フォールバックや
      // 「データが存在しませんでした」は出さず、レート制限の案内のみ表示する
      if (consumeZenrinRateLimitError(error)) {
        return;
      }
      if (hasMapViewingPermission) {
        await whenSearchFail(chibanSearchTerm, {
          isMatch: mapChibanIsMatch,
        });
      } else {
        toast.info("地番データが存在しませんでした");
      }
    }
  };

  /**
   * 検索対象の住居表示を解決する。`addressOverride` 優先、未指定時は state を参照。
   * trim 後に空なら null を返す。手動入力（`addressOverride` 未指定）で空のときのみ
   * エラーバナーを表示し、URL パラメータ経由の自動検索ではサイレントに無視する。
   */
  const resolveSearchAddress = (addressOverride?: string): string | null => {
    const address = addressOverride ?? normalAddressTextBox;
    if (address.trim() === "") {
      if (addressOverride === undefined) {
        toast.error("住居表示を入力してください");
      }
      return null;
    }
    return address;
  };

  /**
   * 住居表示、無料プランの検索処理。
   *
   * @param addressOverride - URL パラメータ経由など、`normalAddressTextBox` ではなく
   *   引数で住居表示を直接指定したい場合の値。省略時は state を参照する。
   * @param saleFallbackTried - 呼び出し元で役所資料（黒塗り）の物件を探し済みなら true（二重に探さない）。
   */
  const handleChangeReverseSearchTermFree = async (
    addressOverride?: string,
    saleFallbackTried = false
  ): Promise<void> => {
    const address = resolveSearchAddress(addressOverride);
    if (address === null) return;
    // 住所として見つからなかったときに、マンション名などで役所資料（黒塗り）の物件を探す。見つかれば地図を動かす
    const showSaleDocumentFallback = async (): Promise<boolean> =>
      !saleFallbackTried && (await showSaleDocumentByKeyword(address));
    try {
      const r = await geospaceReverseFree(address);
      if (r?.lat != null && r?.lng != null) {
        const latlng = new LatLng(r.lat, r.lng);

        // 地図の表示位置をAPI呼び出し結果の座標に移動する
        setViewLatLng(latlng);
        // 検索した場所に役所資料（黒塗り）があればピンが見えるよう、レイヤーをオンにする
        showMunicipalDocumentLayer();
        // ポップアップの状態を設定する
        resetPopupTags("", ""); // 無料プランでは地番情報がないため空文字
        setMarkerState({
          latlng,
          hide: false,
          content: (
            <PopupContent
              addressChiban={"地番は有料プランで表示されます"}
              address={r.address}
              prefName={""}
              cityName={""}
              ooazaName={""}
              azaName={""}
              chiban={""}
              handleKaokuNumberSearch={undefined}
              handleRealEstateDocumentFetchModal={
                handleRealEstateDocumentFetchModal
              }
              kaokuNumberSearchFlag={kaokuNumberSearchFlag as boolean}
              handleNewPropertySheet={handleNewPropertySheet}
              realEstateDocumentFetchFlag={
                realEstateDocumentFetchFlag as boolean
              }
              area={null}
              handlePinSave={handlePinSave}
              initialTags={[]}
              onTagsChange={handleTagsChange}
              crm={crm as boolean}
              hasMapViewingPermission={!!hasMapViewingPermission}
              latlng={latlng}
              availableFields={availableFields}
              hasAvailableFields={hasAvailableFields}
            />
          ),
        });
        // 左のUIの住居表示をAPI呼び出し結果の住居表示で更新
        setNormalAddressTextBox(NYHalfWidthNumberToFillWidthNumber(r.address));
      } else if (!(await showSaleDocumentFallback())) {
        toast.info("データが存在しませんでした");
      }
    } catch (error) {
      const notFound =
        (error as GeoSpaceReverseApiError).response?.status === 400;
      if (notFound && (await showSaleDocumentFallback())) {
        return;
      }
      if (notFound) {
        toast.error(
          "データが見つかりませんでした。入力内容を確認してください。"
        );
      } else {
        toast.error(
          "エラーが発生しました。しばらく経ってから再度お試しください。"
        );
      }
    }
  };

  /**
   * 住居表示の逆引き検索（地番取得 + 地図移動 + ポップアップ表示）。
   *
   * @param addressOverride - URL パラメータ経由など、`normalAddressTextBox` ではなく
   *   引数で住居表示を直接指定したい場合の値。省略時は state を参照する。
   * @param mapModeOverride - レイヤー切り替え直後など、現在のレンダリングサイクルの
   *   `mapMode` ではなく予想値を使いたい場合の値。省略時は state を参照する。
   */
  const handleChangeReverseSearchTerm = async (
    addressOverride?: string,
    mapModeOverride?: MapMode
  ): Promise<void> => {
    const address = resolveSearchAddress(addressOverride);
    if (address === null) return;
    // レースコンディション防止: 関数開始時のチェーンを保持し、await 中にユーザーが
    // レイヤーを切り替えても検索開始時の条件で一貫して処理する。
    // URL 経由の自動検索ではレイヤー反映直後の予想 mapMode（mapModeOverride）で組み直す。
    const chain =
      mapModeOverride === undefined
        ? searchChain
        : buildSearchChain({
            mapFamily: mapModeOverride,
            sources: searchSourceStates,
          });
    try {
      // ゼンリンが検索語を弾いた理由。他のソースも当たらなかったときだけ出す。
      let zenrinRejectedMessage: string | undefined;

      // チェーンを先頭から順に引き、最初のヒットで打ち切る。副作用（地図移動・
      // ポップアップ・トースト・whenSearchFail）はこの下で結果 kind に応じて適用する。
      const resolution = await runSearchChain({
        chain,
        lookup: buildAddressLookup(address, {
          onZenrinRejected: (message) => {
            zenrinRejectedMessage = message;
          },
        }),
      });

      // レート制限で打ち切ったときは、無料住所検索・Google フォールバックも呼ばない
      if (resolution.kind === "rateLimited") {
        toast.warning(RATE_LIMITED_MESSAGE);
        return;
      }

      // ゼンリンが検索語を弾いて他も当たらなかったときは、理由のトーストだけで終える。
      // 他のソースで当たった・座標が分かったときは、その結果の表示だけにする。
      if (
        resolution.kind === "notFound" &&
        zenrinRejectedMessage !== undefined
      ) {
        toast.error(zenrinRejectedMessage);
        return;
      }

      if (resolution.kind === "hit") {
        const r = resolution.result;
        const latlng = new LatLng(r.lat, r.lng);
        // 地図の表示位置をAPI呼び出し結果の座標に移動する
        setViewLatLng(latlng);
        // 検索した場所に役所資料（黒塗り）があればピンが見えるよう、レイヤーをオンにする（既にオンなら何もしない）
        showMunicipalDocumentLayer();
        // API呼び出し結果のポリゴンを表示する
        setPolygonData(r.polygon?.coordinates ?? []);
        // ポップアップの状態を設定する
        const {
          existingTags,
          existingAdditionalFields,
          builtLocation,
          chiban,
        } = addOrFindMapClickProperty({
          ...mapClickPropertyParams,
          searchResult: r,
        });
        setMarkerState({
          latlng,
          hide: false,
          content: (
            <PopupContent
              addressChiban={r.addressChiban}
              address={r.address}
              prefName={r.prefName}
              cityName={r.cityName}
              ooazaName={r.ooazaName ?? ""}
              azaName={r.azaName ?? ""}
              chiban={r.chiban}
              handleKaokuNumberSearch={handleKaokuNumberSearch}
              handleRealEstateDocumentFetchModal={
                handleRealEstateDocumentFetchModal
              }
              kaokuNumberSearchFlag={kaokuNumberSearchFlag as boolean}
              handleNewPropertySheet={handleNewPropertySheet}
              realEstateDocumentFetchFlag={
                realEstateDocumentFetchFlag as boolean
              }
              area={r.area}
              handlePinSave={handlePinSave}
              initialTags={existingTags}
              onTagsChange={handleTagsChange}
              initialAdditionalFields={existingAdditionalFields}
              onAdditionalFieldsChange={(additionalFields) => {
                handleMapClickAdditionalFieldsChange(
                  additionalFields,
                  builtLocation,
                  chiban,
                  r?.prefName ?? ""
                );
              }}
              crm={crm as boolean}
              hasMapViewingPermission={!!hasMapViewingPermission}
              latlng={latlng}
              availableFields={availableFields}
              hasAvailableFields={hasAvailableFields}
              sourceLabel={SEARCH_SOURCE_LABEL[resolution.sourceId]}
            />
          ),
        });
        // 左のUIの地番をAPI呼び出した結果の地番で更新
        setChibanAddressTextBox(
          NYHalfWidthNumberToFillWidthNumber(r.addressChiban)
        );
        // 住居表示は結果が持つ場合のみ上書きする。TRUSTART は住居表示を持たない
        // （r.address が空）が、住居表示検索ではユーザーが入力した住所が分かっている
        // ので、それを残して「住居表示↔地番」を両ボックスに併記する。
        if (r.address) {
          setNormalAddressTextBox(
            NYHalfWidthNumberToFillWidthNumber(r.address)
          );
        }
        notifyIfSourceMismatch(resolution);
      } else if (resolution.kind === "geocodedPin") {
        // 内部地番図が筆ミスで返した座標へピン（後続ソースも外した後の最終フォールバック）
        clearChibanTextBoxOnFallback();
        showBackendGeocodedPin(resolution.geocoded);
        showMunicipalDocumentLayer();
      } else if (await showSaleDocumentByKeyword(address)) {
        // 住所としては見つからなかったが、マンション名などで役所資料（黒塗り）の物件が見つかった。
        // その物件へ地図を動かしてレイヤーをオンにしたので、「見つからない」の案内は出さない
      } else if (resolution.kind === "notAvailable") {
        // 非提供エリアで他ソース（ゼンリン等）も当たらなかったときのみ表示。
        // ピンを差し替えないので地番テキストボックスもそのまま残す。
        toast.info(resolution.message);
      } else if (resolution.geocodeFailed) {
        // 役所系が BE で同じ住所の Google ジオコーディングに失敗済み。呼び直しても結果は
        // 変わらないので、Google を使うフォールバック（無料住所検索・whenSearchFail）は省く。
        clearChibanTextBoxOnFallback();
        toast.info("地番データが存在しませんでした");
      } else if (!hasMapViewingPermission) {
        // 無料ユーザー: どのソースもミスしたら、既存の無料住所検索へフォールバックする。
        // 役所資料（黒塗り）はこの上で探し済み
        clearChibanTextBoxOnFallback();
        await handleChangeReverseSearchTermFree(address, true);
      } else {
        // 有料でどのソースもミス。Google ジオコーディングは単一検索の最終段として
        // レイヤー構成によらず呼ぶ（二重実行の回避は geocodedPin / geocodeFailed 分岐が担う）。
        clearChibanTextBoxOnFallback();
        await whenSearchFail(address);
      }
    } catch (error) {
      // ゼンリンのレート制限(429)は「データなし」ではないため、Google フォールバックや
      // 汎用エラーは出さず、レート制限の案内のみ表示する
      if (consumeZenrinRateLimitError(error)) {
        return;
      }
      const notFound =
        (error as GeoSpaceReverseApiError).response?.status === 400;
      // 住所として見つからない（400）ときも、役所資料（黒塗り）の物件を探す
      if (notFound && (await showSaleDocumentByKeyword(address))) {
        return;
      }
      if (notFound) {
        toast.error(
          "データが見つかりませんでした。入力内容を確認してください。"
        );
      } else {
        toast.error(
          "エラーが発生しました。しばらく経ってから再度お試しください。"
        );
      }
      // 無料ユーザーは有料 API（whenSearchFail の Google ジオコーディング）を呼ばない
      if (hasMapViewingPermission) {
        clearChibanTextBoxOnFallback();
        await whenSearchFail(address);
      }
    }
  };

  /**
   * URL パラメータ経由の住居表示自動検索のトリガ。
   * 住居表示テキストボックス横の「検索」ボタンを押したのと等価の処理を行う。
   * `useMapUrlParams` から呼ばれる。
   *
   * UI の検索ボタンと同じ条件（`canUseParcelSearch`）で関数を使い分ける:
   *   - 引けるデータソースがある: `handleChangeReverseSearchTerm`（住居表示→地番の逆引き）
   *   - 無い: `handleChangeReverseSearchTermFree`（地番なしで地図移動のみ）
   */
  const triggerAddressSearch = async (
    address: string,
    predictedMapMode: MapMode
  ): Promise<void> => {
    await dispatchAddressSearch({
      address,
      predictedMapMode,
      canUseParcelSearch,
      reverseSearch: handleChangeReverseSearchTerm,
      reverseSearchFree: handleChangeReverseSearchTermFree,
    });
  };

  /**
   * URL パラメータ経由の登記地番自動検索のトリガ（Salesforce SSO 引き継ぎ用途）。
   * 登記地番テキストボックス横の「検索」ボタンを押したのと等価の処理を行う。
   * `useMapUrlParams` から呼ばれる。
   *
   * 検索語は `chiban` 優先・無ければ `kaokuNumber`（解決は `resolveAutoSearchTarget`）。
   * 家屋番号からの地番変換（`POST /kaoku-number/`）は将来対応のため、当面は `kaokuNumber` も
   * 地番と同じ検索語として扱う（実装計画書 4.3 節）。座標が得られない場合はピンを立てず、
   * 検索欄に値が入った状態で利用者に地図上で訂正してもらう（`handleChangeSearchTerm` 既定の挙動）。
   */
  const triggerChibanSearch = async (searchTerm: string): Promise<void> => {
    await handleChangeSearchTerm(searchTerm);
  };

  // 地図画面の URL クエリパラメータ初期化処理（address / chiban / kaokuNumber /
  // chibanchizu / requestedPicturesList）。Salesforce SSO 引き継ぎ（機能1）を受けた
  // 自動検索の入り口。パラメータの追加・削除は `utils/urlParams/schema.ts` の
  // スキーマ宣言で一元管理する。既存 stateKey useEffect (上述) とは独立した
  // useEffect として並行動作する。
  useMapUrlParams({
    mapMode,
    hasZenrinContract,
    hasGeospaceContract,
    setNormalAddressTextBox,
    triggerAddressSearch,
    setChibanAddressTextBox,
    triggerChibanSearch,
    // URL の address パラメータが上限超過のときはサイレント無視せず警告通知する。
    onAddressTooLong: () => {
      toast.warning(
        `URL の住居表示パラメータ（address）は${CHIBAN_SEARCH_MAX_LENGTH}文字以内で指定してください。`
      );
    },
  });

  // Map側で地図がクリックされ、clickedLatLngが更新されたときに実行する
  // APIを呼び出して、LatLng座標から地番情報を取得する
  useEffect(() => {
    if (clickedLatLng === null) return;

    // 引けるデータソースが 1 つも無いときは黙って何もしない（検索ボックスと同じ条件）。
    // 契約も利用可能な役所系ソースも無いアカウントで、クリックのたびに通知が出るのを避ける。
    if (!canUseParcelSearch) return;

    // 物件一覧の行を押して引く時（handleFocusPropertyRow）。その行の筆が引けた時だけ結果を使う
    const focusLookup =
      rowFocusLookupRef.current?.latlng === clickedLatLng
        ? rowFocusLookupRef.current
        : null;

    // 不動産の権利移動から画面から遷移してプロットされた受付帳ピンをクリックした場合は、地番検索処理をスキップする
    // これがないと地番検索が動作し、一度表示したポップアップの内容が上書きされてしまう
    const clickedReceptionBookMarker = receptionBookMarkerRef.current.find(
      (m) => m.latlng?.equals(clickedLatLng)
    );
    if (clickedReceptionBookMarker && !focusLookup) {
      // リストから削除済みでも、クリックで受付帳行を再追加できるようにする
      setSelectedProperties((prev) =>
        ensureReceptionBookPropertyRow({
          marker: clickedReceptionBookMarker,
          rows: prev,
          findDuplicatePropertyIndex,
          mergePropertySelectionRow,
          addToList: !!hasMapViewingPermission,
          buildLocationString,
        })
      );
      return;
    }

    // 次のクリック（またはクリア）が来たら、この地点の結果は反映しない。
    // チェーンを順に叩くため、連打すると前の地点の検索が後から終わることがある。
    let cancelled = false;

    (async () => {
      // 単一検索の共通チェーンをそのまま使う。クリックは座標が既に分かっているので、
      // 全ソースが外れても Google ジオコーディングへは飛ばさない。
      let resolution: Awaited<ReturnType<typeof runSearchChain>>;
      try {
        resolution = await runSearchChain({
          chain: searchChain,
          lookup: buildLatLngLookup(clickedLatLng.lat, clickedLatLng.lng),
        });
      } catch (error) {
        // ゼンリンのレート制限(429)は「データなし」ではないため、レート制限の案内のみ表示する
        if (consumeZenrinRateLimitError(error)) {
          return;
        }
        throw error;
      }
      if (cancelled) return;

      const r = resolution.kind === "hit" ? resolution.result : null;

      if (focusLookup) {
        const rowIndex = r
          ? findDuplicatePropertyIndex(
              selectedProperties,
              "LAND",
              r.prefName,
              buildLocationString(r),
              r.chiban
            )
          : -1;
        const row = selectedProperties[rowIndex];
        // 引けた筆が押した行の物件でなければ（隣の筆にかかった・引けなかった）、一覧に足さず、
        // 通知も出さない。行の青ピンを選択中にして地図を動かしただけにする。
        // 検索結果・保存した物件リストなど別の種類のピンの行も同じ（地図クリックの青ピンとは
        // 結び付かず、出してもすぐ消えるため）
        if (
          row?.id !== focusLookup.rowId ||
          (row.pinType !== undefined && row.pinType !== "map-click")
        ) {
          return;
        }
      }

      if (r) {
        // 地番 API のレスポンスが受付帳ピンの同一地番か確認
        const matchingReceptionBook = receptionBookMarkerRef.current.find(
          (m) => {
            if (!m.chibanRawData) return false;
            const cd = m.chibanRawData;
            return (
              cd.prefName === r.prefName &&
              normalizeChibanKaokuNumber(cd.chiban) ===
                normalizeChibanKaokuNumber(r.chiban) &&
              normalizeLocation(buildLocationString(cd)) ===
                normalizeLocation(buildLocationString(r))
            );
          }
        );

        // 受付帳ピンと同一地番の場合、リストに無ければ受付帳行を追加した上でポップアップを開く
        if (matchingReceptionBook?._id !== undefined) {
          setSelectedProperties((prev) =>
            ensureReceptionBookPropertyRow({
              marker: matchingReceptionBook,
              rows: prev,
              findDuplicatePropertyIndex,
              mergePropertySelectionRow,
              addToList: !!hasMapViewingPermission,
              buildLocationString,
            })
          );
          openReceptionBookPopupRef.current?.(matchingReceptionBook._id);
          return;
        }

        notifyIfSourceMismatch(resolution);
        // ポリゴンを設定
        setPolygonData(r.polygon?.coordinates ?? []);
        // 地図のクリック場所の情報をUI左側のテキストボックスに反映する
        setNormalAddressTextBox(NYHalfWidthNumberToFillWidthNumber(r.address));
        setChibanAddressTextBox(
          NYHalfWidthNumberToFillWidthNumber(r.addressChiban)
        );
        // 登記取得物件選択テーブルに追加
        const {
          existingTags,
          existingRemarks,
          existingAdditionalFields,
          builtLocation,
          chiban,
        } = addOrFindMapClickProperty({
          ...mapClickPropertyParams,
          searchResult: r,
        });

        setMarkerState((prevState: MarkerState) => {
          return {
            ...prevState,
            latlng: clickedLatLng,
            hide: false,
            content: (
              <PopupContent
                key={`${r.prefName}-${builtLocation}-${chiban}`}
                addressChiban={r.addressChiban}
                address={r.address}
                prefName={r.prefName}
                cityName={r.cityName}
                ooazaName={r.ooazaName ?? ""}
                azaName={r.azaName ?? ""}
                chiban={r.chiban}
                handleKaokuNumberSearch={handleKaokuNumberSearch}
                handleRealEstateDocumentFetchModal={
                  handleRealEstateDocumentFetchModal
                }
                kaokuNumberSearchFlag={kaokuNumberSearchFlag as boolean}
                handleNewPropertySheet={handleNewPropertySheet}
                realEstateDocumentFetchFlag={
                  realEstateDocumentFetchFlag as boolean
                }
                area={r.area}
                handlePinSave={handlePinSave}
                initialTags={existingTags}
                onTagsChange={(tags) => {
                  handleBluePinTagsChange(
                    tags,
                    builtLocation,
                    chiban,
                    r.prefName
                  );
                }}
                initialRemarks={existingRemarks}
                onRemarksChange={(remarks) => {
                  handleMapClickRemarksChange(
                    remarks,
                    builtLocation,
                    chiban,
                    r.prefName
                  );
                }}
                initialAdditionalFields={existingAdditionalFields}
                onAdditionalFieldsChange={(additionalFields) => {
                  handleMapClickAdditionalFieldsChange(
                    additionalFields,
                    builtLocation,
                    chiban,
                    r.prefName
                  );
                }}
                crm={crm as boolean}
                hasMapViewingPermission={!!hasMapViewingPermission}
                latlng={clickedLatLng}
                availableFields={availableFields}
                hasAvailableFields={hasAvailableFields}
                sourceLabel={
                  resolution.kind === "hit"
                    ? SEARCH_SOURCE_LABEL[resolution.sourceId]
                    : undefined
                }
              />
            ),
          };
        });
      } else {
        setPolygonData([]);
        setMarkerState((prevState: MarkerState) => {
          return {
            ...prevState,
            latlng: clickedLatLng,
            hide: false,
            content: null,
          };
        });
        // 地番検索・住所検索と同じく、レート制限・非提供エリアならその理由を出す
        if (resolution.kind === "rateLimited") {
          toast.warning(RATE_LIMITED_MESSAGE);
        } else {
          toast.info(
            resolution.kind === "notAvailable"
              ? resolution.message
              : "データが存在しませんでした"
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clickedLatLng]);

  // 登記取得物件選択テーブルのデータが変更されたときに実行する
  // ポリゴンがあるものはポリゴンの輪郭も出し、すべての物件に座標から青ピンを立てる
  // （青ピン＝物件一覧に入っている、で意味を揃えるため。ポリゴンの有無で分けない）。
  // ただし地図クリックの青ピンが表している行は、そのピンが代わりに示すので立てない
  // （筆の代表点とクリックした地点がずれて、青ピンが2本並ぶため）
  useEffect(() => {
    const newPolygons: GeoSpacePolygonItem[][][] = [];
    const newPins: LatLng[] = [];

    selectedProperties.forEach((property) => {
      const shownByMapClickPin = isRowOfMapClickPin(
        property,
        currentPopupIdentifier
      );
      const polygonCoordinates =
        property.bookType === "LAND" &&
        property.rawData &&
        property.rawData.polygon &&
        property.rawData.polygon.coordinates?.length
          ? property.rawData.polygon.coordinates
          : null;

      if (polygonCoordinates && polygonCoordinates.length > 0) {
        newPolygons.push(...toMultiPolygonPositions(polygonCoordinates));
      }

      const position = getPropertyRowPosition(property);
      if (!shownByMapClickPin && position) {
        newPins.push(position);
      }
    });

    setSelectedPropertiesPolygonData(newPolygons);
    setSelectedPropertiesPinData(newPins);
  }, [selectedProperties, currentPopupIdentifier]);

  // ---- 物件一覧の行と地図のピンの対応 ----
  // 行を押すと地図をその物件へ動かして青ピンを選択中の見た目にし、
  // ピンを押してポップアップを開くと一覧の該当行を見せる。どちらも同じ行に色を付ける

  /** 物件一覧で、地図のピンと対応させて色を付けている行 */
  const [focusedPropertyRowId, setFocusedPropertyRowId] = useState<
    number | null
  >(null);

  // 一覧から消えた行は対応を外す
  useEffect(() => {
    if (
      focusedPropertyRowId !== null &&
      !selectedProperties.some((row) => row.id === focusedPropertyRowId)
    ) {
      setFocusedPropertyRowId(null);
    }
  }, [selectedProperties, focusedPropertyRowId]);

  /** 選択中の見た目にする青ピンの地点（地図クリックのピンが表す行は、そのピン自身が示すので出さない） */
  const focusedPropertyPinPosition = useMemo(() => {
    const row = selectedProperties.find((r) => r.id === focusedPropertyRowId);
    if (!row || isRowOfMapClickPin(row, currentPopupIdentifier)) return null;
    return getPropertyRowPosition(row);
  }, [selectedProperties, focusedPropertyRowId, currentPopupIdentifier]);

  // 色を付けている行。行を押してすぐ同期で開くポップアップ（popupopen）からも、押した行が見えるように ref でも持つ
  const focusedRowIdRef = useRef<number | null>(focusedPropertyRowId);
  focusedRowIdRef.current = focusedPropertyRowId;
  const focusPropertyRow = (rowId: number | null): void => {
    focusedRowIdRef.current = rowId;
    setFocusedPropertyRowId(rowId);
  };

  /**
   * 物件一覧の行を押した時。地図をその物件へ動かし（いまより引いている時は寄せる）、
   * その地点のピンを押した時と同じくポップアップを出す（ほかのポップアップは閉じる）。
   * 検索結果・保存した物件リスト・受付帳・地図クリックのピンがあれば、そのポップアップを開く。
   *
   * 範囲検索・CSV で入れた行などピンが無い土地は、その地点の地番を引き、引けた筆がその行の物件なら
   * 地図クリックと同じくポップアップを出す。別の筆だったり建物だったりする時は、一覧には何も足さず、
   * 地図を動かして青ピンを選択中にするだけにする（行を見ただけで登記取得の対象を増やさないため）
   *
   * moveMap: false は地図を動かさない（地図の青ピンを押した時）
   */
  const handleFocusPropertyRow = useCallback(
    (row: PropertySelectionRow, { moveMap = true } = {}): void => {
      focusPropertyRow(row.id);
      // 地図クリック・住所検索・地番検索の行は、いま立っている青ピン（ポップアップ）の地点
      const position = isRowOfMapClickPin(row, currentPopupIdentifier)
        ? markerState.latlng ?? getPropertyRowPosition(row)
        : getPropertyRowPosition(row);
      if (!map || !position) return;
      if (moveMap) {
        // すぐに動かす。開くポップアップをピンと合わせて中心へ寄せる時に、ズームの途中だと位置がずれるため
        map.setView(
          position,
          Math.max(map.getZoom(), FOCUS_PROPERTY_MIN_ZOOM),
          { animate: false }
        );
      }

      const pin = findPopupPinAtPosition(map, position, row);
      if (pin) {
        const popup = pin.getPopup();
        if (!pin.isPopupOpen()) pin.openPopup();
        // 開いた時は popupopen で寄せる。もう開いていた時はここで寄せる
        else if (popup) schedulePanToPinAndPopup(map, popup);
        return;
      }
      map.closePopup();
      if (row.bookType !== "LAND") return;
      // 地図クリックと同じく地番を引く。結果はその行の筆の時だけ使う（clickedLatLng の effect で見る）
      rowFocusLookupRef.current = { latlng: position, rowId: row.id };
      setClickedLatLng(position);
    },
    // focusPropertyRow は ref と setState だけを使う
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, currentPopupIdentifier, markerState.latlng]
  );

  // 地図クリックで情報を出した物件が一覧にあれば、その行を見せる
  useEffect(() => {
    if (!currentPopupIdentifier) return;
    const row = selectedProperties.find((r) =>
      isRowOfMapClickPin(r, currentPopupIdentifier)
    );
    if (row) focusPropertyRow(row.id);
    // 一覧の変更（タグの編集など）では動かさない。情報を出す物件が変わった時だけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPopupIdentifier]);

  /**
   * ポップアップを開いたのに、まだ一覧に行が無いピン。
   * ピンを押すとポップアップが先に開き、行はその後のクリックの処理（または受付帳の地番検索）で入るので、
   * 行が入った時に引き直す。別のポップアップに替わったら捨てる
   */
  const pendingPopupPinRef = useRef<{
    position: LatLng;
    property: PinProperty | undefined;
  } | null>(null);

  // ピンを押してポップアップが開いたら、一覧の該当行を見せる。
  // 検索結果・保存した物件リストのピンは物件を持っているので、それで行を引く。
  // 物件を持たないピン（受付帳など）は、一覧の行と同じ座標に立っているので座標で引く。
  // 地図クリックのピンはクリックした地点にあるので、情報を出している物件（地番）で引く
  const focusRowFromPopupRef = useRef<((pin: L.Layer) => void) | null>(null);
  focusRowFromPopupRef.current = (pin: L.Layer) => {
    pendingPopupPinRef.current = null;
    const position = (pin as L.Layer & { getLatLng: () => LatLng }).getLatLng();
    const property = getPinProperty(pin);
    const isMapClickPin =
      property === undefined &&
      !markerState.hide &&
      !!markerState.latlng?.equals(position, SAME_POSITION_MARGIN);
    if (isMapClickPin) {
      const row = selectedProperties.find((r) =>
        isRowOfMapClickPin(r, currentPopupIdentifier)
      );
      if (row) focusPropertyRow(row.id);
      return;
    }
    const rowId = findPropertyRowIdForPin(
      selectedProperties,
      position,
      property,
      focusedRowIdRef.current
    );
    if (rowId === null) {
      // 前の行の色を残すと、選択中のピンが2本になるので外す
      focusPropertyRow(null);
      pendingPopupPinRef.current = { position, property };
      return;
    }
    focusPropertyRow(rowId);
  };
  useEffect(() => {
    const pending = pendingPopupPinRef.current;
    if (!pending) return;
    const rowId = findPropertyRowIdForPin(
      selectedProperties,
      pending.position,
      pending.property,
      null
    );
    if (rowId === null) return;
    pendingPopupPinRef.current = null;
    focusPropertyRow(rowId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperties]);
  useEffect(() => {
    if (!map) return;
    const popupOpenClass = "map-popup-open-sp";
    const handlePopupOpen = (event: L.PopupEvent): void => {
      // SP は地図が狭く、上側のボタンがポップアップに重なるので、開いている間は隠す（map-popup.css）
      if (isMobileRef.current) {
        map.getContainer().classList.add(popupOpenClass);
      }
      const source = (event.popup as L.Popup & { _source?: L.Layer })._source;
      // ピン以外のポップアップ（範囲検索など）は行と対応させず、地図も動かさない
      if (
        !source ||
        typeof (source as { getLatLng?: unknown }).getLatLng !== "function"
      ) {
        return;
      }
      focusRowFromPopupRef.current?.(source);
      // ピンのポップアップは、ピンと合わせて地図の中心に寄せる
      schedulePanToPinAndPopup(map, event.popup);
    };
    const handlePopupClose = (): void => {
      pendingPopupPinRef.current = null;
      map.getContainer().classList.remove(popupOpenClass);
    };
    map.on("popupopen", handlePopupOpen);
    map.on("popupclose", handlePopupClose);
    return () => {
      map.off("popupopen", handlePopupOpen);
      map.off("popupclose", handlePopupClose);
    };
  }, [map]);

  /**
   * 物件一覧の青ピンを押した時。青ピンは元のピンの上に重ねて描いているので、クリックは青ピンが受け取る。
   * 下に元のピン（ポップアップを持つ、または解析済み登記情報のようにクリックで開く）があれば、
   * それを押したのと同じにする。
   * 無ければ（範囲検索・CSV で入れた行など）行を押したのと同じにする（地図は動かさない）。
   * 同じ地点に行が並ぶ時は、色を付けている行を優先する
   */
  const handleListedPropertyPinClick = useCallback(
    (position: LatLng, event: L.LeafletMouseEvent): void => {
      if (!map) return;
      const rowsAtPin = selectedProperties.filter((r) =>
        getPropertyRowPosition(r)?.equals(position, SAME_POSITION_MARGIN)
      );
      const row =
        rowsAtPin.find((r) => r.id === focusedRowIdRef.current) ?? rowsAtPin[0];
      if (!row) return;
      const pin =
        findPopupPinAtPosition(map, position, row) ??
        findClickPinAtPosition(map, position, event.target as L.Layer);
      if (pin) {
        pin.fire("click", {
          latlng: position,
          layerPoint: event.layerPoint,
          containerPoint: event.containerPoint,
          originalEvent: event.originalEvent,
        });
        return;
      }
      handleFocusPropertyRow(row, { moveMap: false });
    },
    [map, selectedProperties, handleFocusPropertyRow]
  );

  const buildLocationString = useCallback((r: GeoSpaceChiban): string => {
    const ooazaName = r.ooazaName ?? "";
    const azaName = r.azaName ?? "";
    return `${r.cityName}${ooazaName}${azaName}`;
  }, []);

  const findDuplicatePropertyIndex = useCallback(
    (
      rows: PropertySelectionRow[],
      bookType: BookTypeEng,
      prefName: string,
      location: string,
      chiban: string
    ): number => {
      const normalizedLocation = normalizeLocation(location);
      const normalizedChiban = normalizeChibanKaokuNumber(chiban);
      return rows.findIndex((row) => {
        return (
          row.bookType === bookType &&
          row.prefName === prefName &&
          normalizeLocation(row.location) === normalizedLocation &&
          normalizeChibanKaokuNumber(row.chibanKaokuNumber) === normalizedChiban
        );
      });
    },
    []
  );

  const mergePropertySelectionRow = useCallback(
    (
      rows: PropertySelectionRow[],
      index: number,
      area: number | null,
      rawData?: GeoSpaceChiban
    ): PropertySelectionRow[] | undefined => {
      const target = rows[index];
      let updatedRow = target;
      let shouldUpdate = false;

      if (area != null && target.area == null) {
        updatedRow = { ...updatedRow, area };
        shouldUpdate = true;
      }

      if (rawData && !updatedRow.rawData) {
        updatedRow = { ...updatedRow, rawData };
        shouldUpdate = true;
      }

      if (!shouldUpdate) return undefined;

      const newRows = [...rows];
      newRows[index] = updatedRow;
      return newRows;
    },
    []
  );

  const checkDuplicateProperties = useCallback(
    (
      rows: PropertySelectionRow[],
      bookType: BookTypeEng,
      prefName: string,
      location: string,
      chiban: string,
      _area: number | null = null
    ): boolean => {
      return (
        findDuplicatePropertyIndex(
          rows,
          bookType,
          prefName,
          location,
          chiban
        ) !== -1
      );
    },
    [findDuplicatePropertyIndex]
  );

  // 地図クリック時の物件管理処理で使用するパラメータ
  const mapClickPropertyParams: Omit<
    AddOrFindMapClickPropertyParams,
    "searchResult"
  > = {
    selectedProperties,
    setSelectedProperties,
    setCurrentPopupRowId,
    setPopupInfo: setCurrentPopupIdentifier,
    buildLocationString,
    findDuplicatePropertyIndex,
    mergePropertySelectionRow,
    // 無料ユーザー（地図閲覧権限なし）は左下の物件選択リストが無効化されているため追加しない
    addToList: !!hasMapViewingPermission,
  };

  /** 物件一覧に、登記を取得する物件（チェックが付いた行）があるか */
  const hasAcquireTarget = selectedProperties.some((row) => row.selected);

  const handleClickAcquireButton = (): void => {
    const selected = selectedProperties.filter((row) => row.selected);
    if (selected.length === 0) {
      toast.info("取得対象が選択されていません");
      return;
    }
    if (prefectures.length === 0) {
      toast.info("都道府県データが取得できませんでした");
      return;
    }
    setAcquireMultipleBooksRows(
      selected.map((row) => {
        const pref = prefectures.find((p) => p.name === row.prefName);
        return {
          id: row.id,
          bookType: row.bookType,
          prefCode: pref?.prefCode ?? "13",
          prefName: row.prefName,
          locationFull: row.location,
          chibanKaokuNumber: row.chibanKaokuNumber,
          kyoutan: false,
          sintaku: false,
          genzai: false,
          ownerInfo: false,
          electricDrawings: false,
          tisekiDrawings: false,
          tiekiDrawings: false,
          buildingDrawings: false,
        };
      })
    );

    setAcquireMultipleBooksModalIsOpen(true);
  };

  const handleClickImportReceptionBookMarkers = (): void => {
    // 投影時は既存と同様にリストへ反映する（クリック再追加と同一の行生成ロジックを使う）
    let newRows = [...selectedProperties];
    receptionBookMarkerRef.current.forEach((marker) => {
      newRows = ensureReceptionBookPropertyRow({
        marker,
        rows: newRows,
        findDuplicatePropertyIndex,
        mergePropertySelectionRow,
        addToList: true,
        buildLocationString,
      });
    });
    setSelectedProperties(newRows);
  };

  const handleKaokuNumberSearch = (chibanList: ChibanInput[]): void => {
    kaokuSearchSourcesRef.current = chibanList.flatMap(
      ({ prefectureName, location, latitude, longitude }) =>
        latitude != null && longitude != null
          ? [
              {
                prefName: prefectureName,
                location,
                lat: latitude,
                lng: longitude,
              },
            ]
          : []
    );
    setKaokuNumberSearchChibanList(chibanList);
    setKaokuNumberSearchModalIsOpen(true);
  };

  // 物件概要書生成モーダル表示ハンドラ
  const handleNewPropertySheet = (adi: AddressIntegrator): void => {
    setNewPropertySummaryAdi(adi); // ADIを設定して住所情報を渡す
    setNewPropertySummeryModalIsOpen(true);
  };

  const hasSelectable = selectedProperties.some((property) =>
    ["LAND", "BUILDING", "CONDOMINIUM", "ONE_BUILDING"].includes(
      property.bookType
    )
  );

  const handleRealEstateDocumentFetchModal = (
    prefecture: string,
    address: string,
    chibanOrKaokuNumber: string,
    location: string,
    fromPopup = false,
    bookType?: "LAND" | "BUILDING" | "CONDOMINIUM" | "ONE_BUILDING"
  ): void => {
    if (fromPopup) {
      // ポップアップからの呼び出し時：bookTypeに応じて地番リストまたは家屋番号リストに設定
      // LAND以外（BUILDING, CONDOMINIUM, ONE_BUILDING）は家屋番号として扱う
      const isLand = bookType === "LAND" || bookType === undefined;
      if (isLand) {
        setRealEstateDocumentFetchModalChibanList([
          {
            prefName: prefecture,
            location,
            chibanAndKaokuNumber: chibanOrKaokuNumber,
          },
        ]);
        setRealEstateDocumentFetchModalKaokuNumberList([]);
      } else {
        // 建物・区分建物の場合は家屋番号リストに設定
        setRealEstateDocumentFetchModalChibanList([]);
        setRealEstateDocumentFetchModalKaokuNumberList([
          {
            prefName: prefecture,
            location,
            chibanAndKaokuNumber: chibanOrKaokuNumber,
          },
        ]);
      }
    } else {
      // ボタンからの呼び出し時：選択された土地を表示
      const selectedLand = selectedProperties.filter(
        (row) => row.selected && row.bookType === "LAND"
      );
      setRealEstateDocumentFetchModalChibanList([
        // クリックした地番も含める場合は以下のコメントを外す
        // {
        //   prefName: prefecture,
        //   location: location,
        //   chibanAndKaokuNumber: chibanOrKaokuNumber,
        // },
        ...selectedLand.map((row) => ({
          prefName: row.prefName,
          location: row.location,
          chibanAndKaokuNumber: row.chibanKaokuNumber,
        })),
      ]);

      // ボタンからの呼び出し時：選択された建物を表示
      const selectedBuilding = selectedProperties.filter(
        (row) => row.selected && row.bookType === "BUILDING"
      );
      setRealEstateDocumentFetchModalKaokuNumberList(
        selectedBuilding.map((row) => ({
          prefName: row.prefName,
          location: row.location,
          chibanAndKaokuNumber: row.chibanKaokuNumber,
        }))
      );
    }

    setRealEstateDocumentFetchModalAddress(address);
    setRealEstateDocumentFetchModalChiban(chibanOrKaokuNumber);
    setRealEstateDocumentFetchModalLocation(location);
    setRealEstateDocumentFetchModalIsOpen(true);
  };

  const handlePinSave = useCallback((): void => {
    setOpenedFromPinSave(true);
    setMapPlotImportOpen(true);
  }, [setMapPlotImportOpen]);

  // 不動産検索結果のタグ変更ハンドラー
  // selectedPropertiesとrealEstateSearchResultの両方を更新
  const handleRealEstateSearchTagsChange = useCallback(
    (index: number, tags: string[]): void => {
      const item = realEstateSearchResult[index];
      if (!item) return;

      // realEstateSearchResultを更新
      setRealEstateSearchResult((prevResults) => {
        return prevResults.map((result, idx) => {
          if (idx === index) {
            return { ...result, tags };
          }
          return result;
        });
      });

      // selectedPropertiesを更新
      setSelectedProperties((prevSelected) => {
        return prevSelected.map((prop) => {
          if (
            prop.pinType === "real-estate-search" &&
            prop.bookType === item.bookType &&
            prop.prefName === item.prefName &&
            prop.location === item.location &&
            prop.chibanKaokuNumber === item.chibanKaokuNumber
          ) {
            return { ...prop, tags };
          }
          return prop;
        });
      });
    },
    [realEstateSearchResult]
  );

  // 不動産検索の結果から一覧に入れた土地の行に、地番のポリゴンを付ける（地図クリックで入れた行と同じく、範囲を地図に描く）。
  // 検索結果は地番の形を持たないので、ピンの地点で地図クリックと同じ検索をし、同じ地番の行にだけ付ける。
  // ピンはクリックの関数が変わるたびに作り直されるので、ref 経由で呼んでクリックの関数を変えない
  const attachSearchResultPolygonRef = useRef<(index: number) => void>(
    () => undefined
  );
  attachSearchResultPolygonRef.current = (index: number) => {
    const item = realEstateSearchResult[index];
    if (!item || item.bookType !== "LAND" || !canUseParcelSearch) return;
    runSearchChain({
      chain: searchChain,
      lookup: buildLatLngLookup(item.latitude, item.longitude),
    })
      .then((resolution) => {
        if (resolution.kind !== "hit") return;
        const parcel = resolution.result;
        setSelectedProperties((prev) =>
          attachParcelPolygon(prev, parcel, buildLocationString(parcel))
        );
      })
      .catch(() => {
        // ポリゴンは描けないだけなので、知らせない
      });
  };

  const realEstateSearchResultRef = useRef(realEstateSearchResult);
  realEstateSearchResultRef.current = realEstateSearchResult;

  // 不動産検索の土地の住居表示を、ピンのポップアップを開いた時に取る（座標ごとに1回だけ）。
  // 取れたら検索結果と物件リストの行に入れ、次に開いた時や物件概要書でそのまま使えるようにする
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const [landAddressLoader] = useState(() =>
    createLandAddressLoader(
      async (lat, lng) =>
        await getRealEstateSearchAddressAPI(apiClientRef.current, lat, lng)
    )
  );
  const loadRealEstateSearchLandAddress = useCallback(
    async (
      item: RealEstateSearchResult
    ): Promise<RealEstateSearchAddressResult> => {
      const result = await landAddressLoader(item.latitude, item.longitude);
      if (result.kind === "found") {
        const property = {
          bookType: item.bookType,
          prefName: item.prefName,
          location: item.location,
          chibanKaokuNumber: item.chibanKaokuNumber,
        };
        setRealEstateSearchResult((prev) =>
          fillLandAddress(prev, property, result.address)
        );
        setSelectedProperties((prev) =>
          fillLandAddress(prev, property, result.address)
        );
      }
      return result;
    },
    [landAddressLoader]
  );

  // 不動産検索結果のピンクリックハンドラー（リストへ自動追加）
  const handleRealEstateSearchPinClick = useCallback(
    (index: number): void => {
      // 最新の検索結果は ref から読む。setState の updater の中で別の setState を呼ぶと、
      // 先にポップアップを開いた時の state 更新と重なった時に、追加した行が捨てられるため
      const item = realEstateSearchResultRef.current[index];
      if (!item) return;

      // 重複チェック
      setSelectedProperties((currentSelected) => {
        const duplicateIndex = findDuplicatePropertyIndex(
          currentSelected,
          item.bookType,
          item.prefName,
          item.location,
          item.chibanKaokuNumber
        );

        if (duplicateIndex !== -1) {
          const merged = mergePropertySelectionRow(
            currentSelected,
            duplicateIndex,
            item.area,
            {
              address: item.address,
              lat: item.latitude,
              lng: item.longitude,
              prefName: item.prefName,
              cityName: item.cityName,
              ooazaName: null,
              azaName: null,
              chiban: item.chibanKaokuNumber,
              addressChiban: "",
              polygon: undefined,
              area: item.area,
            }
          );
          return merged ?? currentSelected;
        }

        const normalizedLocation = normalizeLocation(item.location);
        const normalizedChiban = normalizeChibanKaokuNumber(
          item.chibanKaokuNumber
        );
        const newProperty: PropertySelectionRow = {
          id: generateNextId(currentSelected),
          selected: true,
          bookType: item.bookType,
          prefName: item.prefName,
          location: normalizedLocation,
          chibanKaokuNumber: normalizedChiban,
          area: item.area,
          isFeedOrigin: false,
          tags: item.tags,
          remarks: item.remarks,
          pinType: "real-estate-search",
          // 住居表示をまだ取っていない土地は、取るまで保存で null を送る
          ...(item.landAddressPending ? { landAddressPending: true } : {}),
          rawData: {
            address: item.address,
            lat: item.latitude,
            lng: item.longitude,
            prefName: item.prefName,
            cityName: item.cityName,
            ooazaName: null,
            azaName: null,
            chiban: normalizedChiban,
            addressChiban: "",
            polygon: undefined,
            area: item.area,
          },
        };

        return [...currentSelected, newProperty];
      });
      attachSearchResultPolygonRef.current(index);
    },
    [findDuplicatePropertyIndex, mergePropertySelectionRow]
  );

  // realEstateSearchResultの表示用データをselectedPropertiesから派生
  // selectedPropertiesを単一の情報源とし、双方向同期を排除
  const displayRealEstateSearchResult = useMemo(() => {
    return realEstateSearchResult.map((result) => {
      // selectedPropertiesから対応するアイテムを探す
      const selectedItem = selectedProperties.find(
        (prop) =>
          prop.pinType === "real-estate-search" &&
          prop.bookType === result.bookType &&
          prop.prefName === result.prefName &&
          prop.location === result.location &&
          prop.chibanKaokuNumber === result.chibanKaokuNumber
      );

      // selectedPropertiesに存在する場合は、そちらのタグと備考を使用
      if (selectedItem) {
        return {
          ...result,
          tags: selectedItem.tags,
          remarks: selectedItem.remarks,
        };
      }

      return result;
    });
  }, [realEstateSearchResult, selectedProperties]);

  const hasLand = selectedProperties.some(
    (property) => property.bookType === "LAND"
  );

  const handleClickKaokuNumberSearchFromSelectedProperties = (): void => {
    const buildingLike = selectedProperties.filter(
      (property) => property.bookType !== "LAND"
    );
    const lands = selectedProperties.filter(
      (property) => property.bookType === "LAND"
    );

    // 土地と建物の両方がある場合は、両方を含める
    const allProperties = [...lands, ...buildingLike];

    if (allProperties.length === 0) {
      toast.info("検索対象が選択されていません");
      return;
    }

    const chibanListWithType: ChibanInput[] = allProperties.map((row) => {
      const position = getPropertyRowPosition(row);
      return {
        prefectureName: row.prefName,
        location: row.location,
        chiban: row.chibanKaokuNumber,
        bookType: row.bookType,
        latitude: position?.lat,
        longitude: position?.lng,
      };
    });

    handleKaokuNumberSearch(chibanListWithType);
  };

  // 物件リストに物件を追加する処理
  // 重複チェックを行い、重複していない場合のみ追加する
  const appendPropertySelectionRows = (rows: PropertySelectionRow[]): void => {
    setSelectedProperties((prev) => {
      const lastId: number =
        prev.length > 0 ? Math.max(...prev.map((row) => row.id)) : 0;
      let newId = lastId + 1;

      // 入力データを正規化し、検索結果の行には検索元の座標を持たせる（物件リストの保存で座標が必須のため）
      const normalizedRows = rows.map((row) =>
        withKaokuSearchSourcePosition(
          {
            ...row,
            location: normalizeLocation(row.location),
            chibanKaokuNumber: normalizeChibanKaokuNumber(
              row.chibanKaokuNumber
            ),
          },
          kaokuSearchSourcesRef.current
        )
      );

      // rows内の重複をフィルタリング（正規化済みなので直接比較）
      const uniqueRows = normalizedRows.filter((row, index, self) => {
        return (
          index ===
          self.findIndex(
            (r) =>
              r.bookType === row.bookType &&
              r.prefName === row.prefName &&
              r.location === row.location &&
              r.chibanKaokuNumber === row.chibanKaokuNumber
          )
        );
      });

      let nextRows = [...prev];
      const rowsToAdd: PropertySelectionRow[] = [];

      uniqueRows.forEach((row) => {
        const duplicateIndex = findDuplicatePropertyIndex(
          nextRows,
          row.bookType,
          row.prefName,
          row.location,
          row.chibanKaokuNumber
        );

        if (duplicateIndex !== -1) {
          const merged = mergePropertySelectionRow(
            nextRows,
            duplicateIndex,
            row.area,
            row.rawData
          );

          if (merged) {
            nextRows = merged;
          }

          return;
        }

        rowsToAdd.push(row);
      });

      const rowsWithId = rowsToAdd.map((row) => ({
        ...row,
        id: newId++,
        selected: true,
      }));

      return [...nextRows, ...rowsWithId].sort((a, b) => a.id - b.id);
    });
  };

  /**
   * 背景をグレーにするCSSを返す関数
   * @param disabled グレーアウトするかしないかを決めるフラグ
   * @return disabled===true: グレーアウトされたSXを返す、disabled===false: グレーアウト無しのSXを返す
   */
  const getGrayOutSx = (disabled: boolean): SxProps => {
    if (disabled) {
      return {
        pointerEvents: "none",
        opacity: "0.5",
        backgroundColor: "lightgray",
      };
    } else {
      return {
        pointerEvents: "auto",
        opacity: "1",
        backgroundColor: "inherit",
      };
    }
  };

  /**
   * CSVアップロード成功時の処理
   * ピン情報の保存処理が完了したことをトーストメッセージで表示
   */
  const handleCsvUploadSuccess = (): void => {
    setCsvUploadLoading(false);
    toast.success("ピン情報の保存処理が完了しました");
  };

  /**
   * 表示中のプロットデータ（Chip）をCSVまたはExcel形式でエクスポートする処理
   * 指定された物件リストIDの詳細情報をAPI経由で取得し、共通エクスポートユーティリティを呼び出します。
   *
   * @param id - 物件リスト（MapPlotRequest）のID
   * @param name - 物件リストの名称（ダウンロードファイル名に使用）
   * @param formatType - ダウンロードするファイル形式 ('csv' | 'excel')
   */
  const handleDownloadDataset = async (
    id: number,
    name: string,
    formatType: "csv" | "excel"
  ): Promise<void> => {
    try {
      // 該当物件リストの詳細情報（全物件データ、失敗含む）を非同期で取得
      const response = await getPlotRequestDetailAPI(apiClient, id);
      if (!response?.details) {
        toast.error("データの取得に失敗しました。");
        return;
      }

      // 共通エクスポート関数を実行し、ブラウザ経由でダウンロードを実行
      exportPlotRequestDetails(
        formatType,
        name,
        response.details,
        availableFields
      );
    } catch (error) {
      console.error("プロットデータのダウンロードに失敗しました:", error);
      toast.error("データの取得に失敗しました。");
    }
  };

  /**
   * 取引価格を検索する共通ヘルパー関数
   * @param prefectureCode 都道府県コード
   * @param filters フィルタ設定
   */
  const searchTradePrices = async (
    prefectureCode: number,
    filters: TradePriceListFilter
  ): Promise<void> => {
    // 価格情報区分の変換
    const priceClassifications: Array<"01" | "02"> = [];
    if (filters.priceClassifications.includes("realEstateTransaction")) {
      priceClassifications.push("01");
    }
    if (filters.priceClassifications.includes("contractPrice")) {
      priceClassifications.push("02");
    }

    const searchParams = {
      prefecture: prefectureCode,
      priceClassifications: priceClassifications.join(","),
      year: filters.year,
      location: filters.location,
    };

    const results = await searchTradePriceAPI(apiClient, searchParams);
    const rows: TradePriceGridRow[] = results.map(
      (item, index): TradePriceGridRow => ({
        ...item,
        id: `${index}`,
        selected: false,
      })
    );

    setTradePriceListRows(rows);

    if (rows.length === 0) {
      toast.info("条件に一致する取引価格情報が見つかりませんでした");
    } else {
      toast.success(
        `${rows.length.toLocaleString("ja-JP")}件の取引価格情報が見つかりました`
      );
    }
  };

  /**
   * 取引価格の一覧を表示ボタンの押下処理
   */
  const handleShowTradePriceList = (): void => {
    if (!map) {
      toast.error("地図が読み込まれていません");
      return;
    }

    // モーダルを開く
    setTradePriceListModalOpen(true);
    setTradePriceListLoading(false);
    setTradePriceListShowSearchCondition(tradePriceFilter.prefecture !== null);
  };

  /**
   * 取引価格ポップアップから詳細表示リンクをクリックしたときの処理
   * @param location 住所情報（prefecture, municipality, districtName）
   */
  const handleShowTradePriceDetailList = async (location: {
    prefecture: string;
    municipality: string;
    districtName: string;
  }): Promise<void> => {
    setTradePriceListRows([]);
    setTradePriceListModalOpen(true);
    setTradePriceListLoading(true);
    setTradePriceListShowSearchCondition(true);

    try {
      // 都道府県名から都道府県コードを取得
      const prefecture = prefectures.find(
        (prefecture) => prefecture.name === location.prefecture
      );
      if (!prefecture) {
        toast.error(
          `都道府県「${location.prefecture}」の情報が見つかりませんでした`
        );
        setTradePriceListLoading(false);
        setTradePriceListModalOpen(false);
        return;
      }

      const prefectureCode = Number(prefecture.prefCode);
      const locationString = `${location.municipality}${location.districtName}`;

      // location情報を保存（検索条件表示用）
      setTradePriceListLocation({
        municipality: location.municipality,
        districtName: location.districtName,
      });

      const updatedFilter = {
        ...tradePriceFilter,
        prefecture: prefectureCode,
        location: locationString,
      };
      setTradePriceFilter(updatedFilter);

      await searchTradePrices(prefectureCode, updatedFilter);
    } catch (error) {
      console.error("取引価格情報の取得エラー:", error);
      Sentry.captureException(error);
      toast.error("取引価格情報の取得中にエラーが発生しました");
      setTradePriceListModalOpen(false);
    } finally {
      setTradePriceListLoading(false);
    }
  };

  /**
   * 取引価格フィルター適用ハンドラー
   */
  const handleTradePriceFilterApply = async (
    newFilters: typeof tradePriceFilter
  ): Promise<void> => {
    if (!map) {
      toast.error("地図が読み込まれていません");
      return;
    }

    if (newFilters.prefecture === null) {
      toast.error("都道府県を選択してください");
      return;
    }

    setTradePriceFilter(newFilters);
    setTradePriceListLoading(true);
    setTradePriceListShowSearchCondition(true);

    // locationが設定されている場合は、tradePriceListLocationを更新
    if (newFilters.location) {
      // locationはフリーテキストなので、そのまま保存
      setTradePriceListLocation({
        municipality: "",
        districtName: newFilters.location,
      });
    } else {
      setTradePriceListLocation(null);
    }

    try {
      const prefectureCode = newFilters.prefecture;
      await searchTradePrices(prefectureCode, newFilters);
    } catch (error) {
      console.error("取引価格情報の取得エラー:", error);
      toast.error("取引価格情報の取得中にエラーが発生しました");
    } finally {
      setTradePriceListLoading(false);
    }
  };

  /**
   * 不動産検索結果を登記一括取得ボタンの押下処理
   */
  const handleClickReceptionRealEstateSearchResultButton = (): void => {
    if (prefectures.length === 0) {
      toast.info("都道府県データが取得できませんでした");
      return;
    }
    setAcquireMultipleBooksRows(
      realEstateSearchResult.map((row, index) => {
        const pref = prefectures.find((p) => p.name === row.prefName);
        return {
          id: index + 1,
          bookType: row.bookType,
          prefCode: pref?.prefCode ?? "13",
          prefName: row.prefName,
          locationFull: row.location,
          chibanKaokuNumber: row.chibanKaokuNumber,
          kyoutan: false,
          sintaku: false,
          genzai: false,
          ownerInfo: false,
          electricDrawings: false,
          tisekiDrawings: false,
          tiekiDrawings: false,
          buildingDrawings: false,
        };
      })
    );

    setAcquireMultipleBooksModalIsOpen(true);
  };

  /**
   * 不動産検索結果を追加ボタンの押下処理
   */
  const handleClickDisplayRealEstateSearchResultButton = (): void => {
    let addedCount = 0;
    let skippedCount = 0;

    setSelectedProperties((prev) => {
      let nextRows = [...prev];
      const lastId =
        prev.length > 0 ? Math.max(...prev.map((row) => row.id)) : 0;
      let newId = lastId;

      realEstateSearchResult.forEach((row) => {
        const normalizedLocation = normalizeLocation(row.location);
        const normalizedChiban = normalizeChibanKaokuNumber(
          row.chibanKaokuNumber
        );
        const duplicateIndex = findDuplicatePropertyIndex(
          nextRows,
          row.bookType,
          row.prefName,
          normalizedLocation,
          normalizedChiban
        );

        if (duplicateIndex !== -1) {
          const merged = mergePropertySelectionRow(
            nextRows,
            duplicateIndex,
            row.area,
            row.rawData
          );

          if (merged) {
            nextRows = merged;
          }

          skippedCount += 1;
          return;
        }

        addedCount += 1;
        nextRows = [
          ...nextRows,
          {
            ...row,
            location: normalizedLocation,
            chibanKaokuNumber: normalizedChiban,
            id: ++newId,
            selected: true,
            pinType: "real-estate-search" as const,
          },
        ];
      });

      return nextRows.sort((a, b) => a.id - b.id);
    });

    if (addedCount === 0) {
      toast.info("すべての不動産検索結果が既にリストに存在しています");
    } else if (skippedCount > 0) {
      toast.success(
        `${addedCount.toLocaleString(
          "ja-JP"
        )}件を追加しました（${skippedCount.toLocaleString(
          "ja-JP"
        )}件は重複のためスキップ）`
      );
    } else {
      toast.success(
        `${addedCount.toLocaleString("ja-JP")}件をリストに追加しました`
      );
    }
  };

  const handleCloseRealEstateDocumentFetchModal = (): void => {
    setRealEstateDocumentFetchModalIsOpen(false);
  };

  /**
   * 不動産検索結果を履歴保存ボタンの押下処理
   */
  const handleClickSavePropertyHistoryRealEstateSearchResultButton =
    (): void => {
      if (!mapPropertyListHistory) {
        return;
      }
      const now = new Date();
      const rowId = propertyRowCount + 1;
      setPropertyRowCount(rowId);

      setProperties([
        ...properties,
        {
          id: rowId,
          selected: false,
          count: realEstateSearchResult.length,
          recordName: now.toLocaleString() + "保存分",
          saveDatetime: now.toLocaleString(),
          properties: realEstateSearchResult,
        },
      ]);

      toast.success("ピンを履歴保存しました。");
    };

  useEffect(() => {
    return () => {
      if (zenrinOverlayObjectUrlRef.current) {
        URL.revokeObjectURL(zenrinOverlayObjectUrlRef.current);
      }
    };
  }, []);

  // Zenrin画像オーバーレイ機能: 住宅地図（表札あり）を地図上に表示する機能
  // ユーザーが選択した範囲のZenrin地図画像を取得し、オーバーレイとして表示する
  const canRequestZenrinImage =
    hasZenrinContract && Boolean(hasMapViewingPermission);

  const startZenrinImageSelection = (): void => {
    if (!canRequestZenrinImage) {
      toast.error("地図閲覧権限がありません。");
      return;
    }

    if (zenrinImageSelectionMode || zenrinImageLoading) {
      return;
    }

    setZenrinImageSelectionMode(true);
  };

  const handleZenrinHistoryApply = async (
    zenrinHistory: ZenrinMapRequestHistoryResponse["results"][number]
  ): Promise<void> => {
    if (!canRequestZenrinImage) {
      toast.error("地図閲覧権限がありません。");
      return;
    }

    if (!map) {
      toast.info("地図の読み込み中です。");
      return;
    }

    if (
      !Number.isFinite(zenrinHistory.centerLatitude) ||
      !Number.isFinite(zenrinHistory.centerLongitude) ||
      !Number.isFinite(zenrinHistory.zoomLevel)
    ) {
      toast.error("履歴の座標情報が不正です。");
      return;
    }

    setZenrinImageLoading(true);
    try {
      let imageUrl: string | null = null;

      if (zenrinHistory.imageUuid) {
        const imageBlob = await zenrinGetMapImage(zenrinHistory.imageUuid);
        if (imageBlob) {
          if (zenrinOverlayObjectUrlRef.current) {
            URL.revokeObjectURL(zenrinOverlayObjectUrlRef.current);
          }
          const objectUrl = URL.createObjectURL(imageBlob);
          zenrinOverlayObjectUrlRef.current = objectUrl;
          imageUrl = objectUrl;
        }
      }

      if (!imageUrl) {
        toast.error("住宅地図の画像取得に失敗しました");
        return;
      }

      const centerPosition = new LatLng(
        zenrinHistory.centerLatitude,
        zenrinHistory.centerLongitude
      );

      const bounds = zenrinComputeA3Bounds(centerPosition);

      setZenrinImageOverlay({
        bounds,
        url: imageUrl,
      });
      // 住宅地図(表札なし)を有効化
      overlayControllerRef.current?.setOverlay(MAP_ZENRIN_LAYER_NAMES[0], true);
      setZenrinImageSelectionMode(false);
      setZenrinImageSelectionBounds(null);
      setZenrinRequestHistoryModalIsOpen(false);
      map.setView(centerPosition, zenrinHistory.zoomLevel, { animate: true });
      toast.success("住宅地図（表札あり）の履歴を復元しました");
    } catch (error) {
      toast.error("住宅地図の取得に失敗しました");
      Sentry.captureException(error);
    } finally {
      setZenrinImageLoading(false);
    }
  };

  /**
   * Zenrin画像取得確認処理
   * ユーザーが選択した範囲のZenrin地図画像を取得し、地図上にオーバーレイとして表示する
   * 画像はBlob形式で取得し、ObjectURLとして管理する（メモリリーク防止のためクリーンアップ処理も実装）
   */
  const handleZenrinImageConfirm = async (): Promise<void> => {
    if (!canRequestZenrinImage) {
      toast.error("地図閲覧権限がありません。");
      return;
    }

    if (!zenrinImageSelectionMode) {
      toast.info("枠線を表示してから確定してください。");
      return;
    }

    if (!zenrinImageSelectionBounds) {
      toast.info("枠線の準備中です。少し待ってから再度お試しください。");
      return;
    }

    setZenrinImageLoading(true);
    try {
      const response = await zenrinRequestMapImage(
        zenrinImageSelectionBounds.toBBoxString(),
        zoom,
        ZENRIN_A3_WIDTH_PX,
        ZENRIN_A3_HEIGHT_PX,
        ZENRIN_NAMEPLATE_MAP_TYPE
      );

      const imageUuid = response?.imageUuid;
      const fallbackUrl = response?.imageUrl;

      let imageUrl: string | null = null;

      if (imageUuid) {
        const imageBlob = await zenrinGetMapImage(imageUuid);
        if (imageBlob) {
          if (zenrinOverlayObjectUrlRef.current) {
            URL.revokeObjectURL(zenrinOverlayObjectUrlRef.current);
          }
          const objectUrl = URL.createObjectURL(imageBlob);
          zenrinOverlayObjectUrlRef.current = objectUrl;
          imageUrl = objectUrl;
        }
      }

      if (!imageUrl && fallbackUrl) {
        imageUrl = fallbackUrl;
      }

      if (!imageUrl) {
        toast.error("住宅地図の画像取得に失敗しました");
        return;
      }

      setZenrinImageOverlay({
        bounds: zenrinImageSelectionBounds,
        url: imageUrl,
      });
      setZenrinImageSelectionMode(false);
      toast.success("住宅地図（表札あり）を地図に追加しました");
    } catch (error) {
      Sentry.captureException(error);
      toast.error("住宅地図の取得に失敗しました");
    } finally {
      setZenrinImageLoading(false);
    }
  };

  const cancelZenrinImageSelection = (): void => {
    setZenrinImageSelectionMode(false);
    setZenrinImageSelectionBounds(null);
  };

  // 物件概要書モーダルに住所を渡すためのstate
  const [newPropertySummaryAdi, setNewPropertySummaryAdi] = useState<
    AddressIntegrator | undefined
  >();

  // MapControlContext用の関数
  const closeActivePopup = useCallback((): void => {
    mapRef.current?.closePopup();
  }, []);

  const clearClickedLatLngHandler = useCallback((): void => {
    setClickedLatLng(null);
  }, []);

  // overlay切替を外部からできるようにする
  const registerOverlayController = useCallback(
    (controller: OverlayController | null): void => {
      overlayControllerRef.current = controller;
    },
    []
  );

  // Context用の値をメモ化
  // mapModeをContextに追加し、子コンポーネントでも現在の地図モードを参照可能にする
  const mapControlValue = useMemo(
    () => ({
      closeActivePopup,
      clearClickedLatLng: clearClickedLatLngHandler,
      mapMode,
    }),
    [closeActivePopup, clearClickedLatLngHandler, mapMode]
  );

  /** 取引価格の一覧は、取引価格のレイヤーを地図に出している時だけ帯に出す */
  const canShowTradePriceList = Boolean(
    tradePriceSearch && isTradePriceLayerActive
  );

  /** 地図の上の帯に出すものがあるか。無ければ帯ごと出さず、地図を広く使う */
  const hasMapToolbarItems = Boolean(
    isZenrinLayerActive || canShowTradePriceList || !hasMapViewingPermission
  );

  /** 保存した物件リスト（CRM でない時はピン）を保存・呼び出しできるか */
  const canUseSavedPlots = Boolean(mapPlotCsvImport && hasMapViewingPermission);

  /**
   * 物件リストの操作のボタン群。左の列（2列の格子）と物件一覧の拡大モーダル（横並び）で共有する
   * @param layout grid は2列に並べる（左の列）、row は横に並べて折り返す（拡大モーダル）
   */
  const renderPropertyListActions = (layout: "grid" | "row"): JSX.Element => (
    <Box
      sx={
        layout === "grid"
          ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }
          : { display: "flex", flexWrap: "wrap", gap: 1 }
      }
    >
      <MuiButton
        variant="outlined"
        sx={mapMenuButtonSx}
        onClick={handleClickAcquireButton}
        // 物件一覧で取得する物件が選ばれていない時は押せない（押しても案内が出るだけのため）
        disabled={!hasAcquireTarget}
      >
        登記取得する
      </MuiButton>
      {kaokuNumberSearchFlag && (
        <MuiButton
          variant="outlined"
          sx={mapMenuButtonSx}
          onClick={handleClickKaokuNumberSearchFromSelectedProperties}
          disabled={!hasSelectable}
        >
          地番・家屋番号検索
        </MuiButton>
      )}
      {crm && (
        <MuiButton
          variant="outlined"
          sx={mapMenuButtonSx}
          onClick={() => {
            setOpenedFromPinSave(false);
            setMapPlotImportOpen(true);
          }}
        >
          物件リストを保存する
        </MuiButton>
      )}
      {/* CRM でない時は、物件リストの代わりにピンを CSV で保存する */}
      {canUseSavedPlots && !crm && (
        <MuiButton
          variant="outlined"
          sx={mapMenuButtonSx}
          onClick={() => {
            setCsvUploadOpen(true);
          }}
        >
          ピンを保存する
        </MuiButton>
      )}
      {/* 保存したものの呼び出しは、保存するボタンの隣に置く */}
      {canUseSavedPlots && (
        <MuiButton
          variant="outlined"
          sx={mapMenuButtonSx}
          onClick={() => {
            setSavedPlotModalOpen(true);
          }}
        >
          {crm ? "保存リストを呼び出す" : "ピンを呼び出す"}
        </MuiButton>
      )}
    </Box>
  );

  /** 不動産検索（条件で検索）のボタン群 */
  const realEstateSearchButtons = (
    // 2列に並べる
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
      <MuiButton
        variant="outlined"
        fullWidth
        sx={mapMenuButtonSx}
        onClick={() => {
          setRealEstateSearchDrawerOpen(true);
        }}
      >
        条件検索
      </MuiButton>
      <Box>
        <MuiButton
          variant="outlined"
          fullWidth
          sx={mapMenuButtonSx}
          onClick={handleClickRealEstateSearchResultButton}
          disabled={!realEstateSearchResult.length}
        >
          検索結果
        </MuiButton>
        <Menu
          sx={MAP_MENU_SX}
          anchorEl={openRealEstateSearchResultList}
          open={!!openRealEstateSearchResultList}
          onClose={handleCloseRealEstateSearchResultButton}
          // ボタンのすぐ下に、ボタンと同じ幅で出す
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: { width: openRealEstateSearchResultList?.offsetWidth },
          }}
        >
          <MenuItem onClick={handleClickReceptionRealEstateSearchResultButton}>
            登記一括取得
          </MenuItem>
          <MenuItem onClick={handleClickDisplayRealEstateSearchResultButton}>
            リストに追加
          </MenuItem>
          {mapPropertyListHistory && (
            <MenuItem
              onClick={
                handleClickSavePropertyHistoryRealEstateSearchResultButton
              }
            >
              履歴保存
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );

  return (
    <MapControlContext.Provider value={mapControlValue}>
      <MapSearchDialog
        open={realEstateSearchDrawerOpen}
        setOpen={setRealEstateSearchDrawerOpen}
        setSearchResult={setRealEstateSearchResult}
        setViewLatLng={setViewLatLng}
        hasMapViewingPermission={hasMapViewingPermission}
      />
      <Box sx={MAP_PAGE_SX}>
        {/* PC では左の操作列と右の地図を画面の高さに収め、下に余白を残す。ページ全体はスクロールさせない */}
        <Box sx={MAP_PAGE_COLUMNS_SX}>
          {!mapExportPanelOpen && (
            <Box sx={MAP_SIDE_COLUMN_SX}>
              <Box sx={MAP_SIDE_SECTION_SX}>
                {mapRealestateSearch && hasMapViewingPermission && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ ...MAP_SIDE_HEADING_SX, mb: 0.5 }}
                    >
                      条件で検索
                    </Typography>
                    {realEstateSearchButtons}
                  </Box>
                )}
                <Box>
                  <SearchBox
                    label="住所で検索"
                    placeholder="住居表示"
                    disabled={false}
                    // 引けるデータソースがあれば地番の逆引きへ流す（クリック検索と同じ条件）。
                    // 1 つも無いときだけ、地番なしの無料検索へ流す。
                    handleApiRequest={
                      canUseParcelSearch
                        ? handleChangeReverseSearchTerm
                        : handleChangeReverseSearchTermFree
                    }
                    searchTerm={normalAddressTextBox}
                    setSearchTerm={setNormalAddressTextBox}
                    inputProps={{ maxLength: CHIBAN_SEARCH_MAX_LENGTH }}
                  />
                  {/* 役所資料取得依頼は上の住所（住居表示）で依頼するので、住所の検索欄に付けて置く */}
                  {realEstateDocumentFetchFlag && (
                    <Box sx={MAP_ADDRESS_ACTION_ROW_SX}>
                      <Tooltip
                        title={
                          !normalAddressTextBox.trim()
                            ? "住居表示を入力してください"
                            : ""
                        }
                        placement="top"
                      >
                        <span>
                          <MuiButton
                            startIcon={<DescriptionOutlinedIcon />}
                            sx={{ height: MAP_BUTTON_HEIGHT }}
                            disabled={!normalAddressTextBox.trim()}
                            onClick={() => {
                              // 住居表示・地番・所在を渡してモーダルを開く
                              handleRealEstateDocumentFetchModal(
                                prefectures.length > 0
                                  ? prefectures[0].name
                                  : "",
                                normalAddressTextBox,
                                chibanAddressTextBox,
                                realEstateDocumentFetchModalLocation
                              );
                            }}
                          >
                            この住所で役所資料取得依頼
                          </MuiButton>
                        </span>
                      </Tooltip>
                      {/* 別のページへ移るものは、ボタンでなくリンクにする */}
                      <Link
                        href={REAL_ESTATE_DOCUMENT_FETCH_MANUAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={MAP_ADDRESS_ACTION_LINK_SX}
                      >
                        操作ガイド
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    </Box>
                  )}
                </Box>
                <Box>
                  <SearchBox
                    label="地番で検索"
                    placeholder="登記地番"
                    // 引けるデータソースがあれば有効化する（クリック検索と同じ条件）
                    disabled={!canUseParcelSearch}
                    handleApiRequest={handleChangeSearchTerm}
                    searchTerm={chibanAddressTextBox}
                    setSearchTerm={setChibanAddressTextBox}
                    inputProps={{ maxLength: CHIBAN_SEARCH_MAX_LENGTH }}
                  />
                </Box>
              </Box>
              {/* 上は探す（検索）、下は集めた物件リストの操作。線で分ける */}
              <Divider />
              <Box sx={MAP_SIDE_LIST_SECTION_SX}>
                <Box sx={MAP_SIDE_LIST_HEADER_SX}>
                  <Typography variant="body2" sx={MAP_SIDE_HEADING_SX}>
                    物件リスト
                  </Typography>
                  <Tooltip title="拡大して見る" placement="top">
                    <IconButton
                      // SP は指で押すので、タップ領域の最低限を確保する
                      size={isMobile ? "medium" : "small"}
                      aria-label="物件リストを拡大して見る"
                      onClick={() => {
                        setPropertyListDialogOpen(true);
                      }}
                    >
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{ ...getGrayOutSx(!hasMapViewingPermission), mb: 1.5 }}
                >
                  {renderPropertyListActions("grid")}
                </Box>
                <Box sx={MAP_SIDE_GRID_SX}>
                  {/* 拡大モーダルを開いている間は、表の状態が二重にならないようモーダル側だけに出す */}
                  {!propertyListDialogOpen && (
                    <Box
                      sx={{
                        ...getGrayOutSx(!hasMapViewingPermission),
                        height: "100%",
                      }}
                    >
                      <PropertySelectionGrid
                        rows={selectedProperties}
                        setRows={setSelectedProperties}
                        realEstateSearchResult={displayRealEstateSearchResult}
                        crm={crm as boolean}
                        height="100%"
                        focusedRowId={focusedPropertyRowId}
                        onRowFocus={handleFocusPropertyRow}
                        // SP は拡大モーダルを開かなくても消せるよう、まとめて削除するボタンを一覧の上にも出す
                        bulkDeletable={isMobile}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={MAP_MAIN_COLUMN_SX}>
            {mapPlotCsvImport && hasMapViewingPermission && (
              <SavedPlotModal
                open={SavedPlotModalOpen}
                setOpen={setSavedPlotModalOpen}
                // 地図に出しているリストを外せるのは CRM（LoadedMapPlotBar）だけなので、
                // 足し込みと、出しているリストを含めた上限の判定も CRM の時だけにする
                loadedDatasetIds={
                  crm ? loadedDatasets.map((dataset) => dataset.id) : undefined
                }
                loadedPlotCount={crm ? displayPlotData.length : undefined}
                onPlotSelected={(
                  plotDetails: SavedMapPlotRequestDetail[],
                  datasetInfo?: Array<{ id: number; name: string }>
                ) => {
                  // CRM でない時は外す手段が無いので、従来どおり入れ替える
                  if (!crm || !datasetInfo) {
                    setDisplayPlotData(plotDetails);
                    if (datasetInfo) {
                      setLoadedDatasets(appendLoadedDatasets([], datasetInfo));
                    }
                    return;
                  }
                  // 入れ替えずに、いま出しているリストに足す（不要なものは1つずつ外せる）
                  const next = addLoadedDatasets(
                    { datasets: loadedDatasets, plots: displayPlotData },
                    { datasets: datasetInfo, plots: plotDetails }
                  );
                  setLoadedDatasets(next.datasets);
                  setDisplayPlotData(next.plots);
                }}
              />
            )}
            {tradePriceSearch && (
              <TradePriceListModal
                open={tradePriceListModalOpen}
                setOpen={setTradePriceListModalOpen}
                rows={tradePriceListRows}
                loading={tradePriceListLoading}
                filters={tradePriceFilter}
                onFilterApply={(filters) => {
                  void handleTradePriceFilterApply(filters);
                }}
                onShowOnMap={(location, prefName) => {
                  if (!map) return;

                  void (async (): Promise<void> => {
                    try {
                      // 住所から座標を取得
                      const address = `${prefName}${location}`;
                      const result = await geospaceReverseFree(address);

                      if (result?.lat != null && result?.lng != null) {
                        // 地図を移動してズームレベルを14に設定
                        map.setView({ lat: result.lat, lng: result.lng }, 14);
                        toast.success(`${address}に移動しました`);
                      } else {
                        toast.error(
                          "指定された所在地の座標が見つかりませんでした"
                        );
                      }
                    } catch (error) {
                      console.error("座標取得エラー:", error);
                      toast.error("所在地の座標取得中にエラーが発生しました");
                    }
                  })();
                }}
                location={tradePriceListLocation}
                showSearchCondition={tradePriceListShowSearchCondition}
              />
            )}
            {mapPlotCsvImport && hasMapViewingPermission && !crm && (
              <MapPlotRequestCsvUploadModal
                open={csvUploadOpen}
                setOpen={setCsvUploadOpen}
                modalTitle="地図プロット・CSVアップロード"
                confirmButtonLabel="保存する"
                apiEndpoint="/map/file?type=csv"
                onUploadSuccess={handleCsvUploadSuccess}
                loading={csvUploadLoading}
                setLoading={setCsvUploadLoading}
              />
            )}
            <MapPlotImportModal
              open={mapPlotImportOpen}
              onClose={() => {
                setMapPlotImportOpen(false);
                setOpenedFromPinSave(false);
              }}
              onCsvUploadSuccess={handleCsvUploadSuccess}
              csvUploadLoading={csvUploadLoading}
              setCsvUploadLoading={setCsvUploadLoading}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
              openedFromPinSave={openedFromPinSave}
            />
            {!mapExportPanelOpen && crm && (
              <LoadedMapPlotBar
                mapPlots={loadedDatasets}
                onDownload={handleDownloadDataset}
                onRemove={handleRemoveDataset}
              />
            )}
            {/* 地図に対する操作。地図の枠につなげた帯に置き、左の列（物件リストの操作）と見分けられるようにする */}
            {!mapExportPanelOpen && hasMapToolbarItems && (
              <Box sx={MAP_AREA_TOOLBAR_SX}>
                {/* Zenrinレイヤーがアクティブな場合の機能追加 */}
                {isZenrinLayerActive && (
                  <>
                    {/* Zenrin表札あり地図追加ボタングループ */}
                    <ZenrinNameplateButtonGroup
                      selectionMode={zenrinImageSelectionMode}
                      loading={zenrinImageLoading}
                      canRequestZenrinImage={canRequestZenrinImage}
                      onStart={startZenrinImageSelection}
                      onOpenHistory={() => {
                        setZenrinRequestHistoryModalIsOpen(true);
                      }}
                      onConfirm={handleZenrinImageConfirm}
                      onCancel={cancelZenrinImageSelection}
                    />
                    {/* 地図画像ダウンロード機能: PNG/PDF形式で地図をダウンロード可能 */}
                    <Tooltip
                      title={
                        "表示中のレイヤーを画像として保存します（操作パネルは含みません）"
                      }
                      placement="top"
                    >
                      <span>
                        <MuiButton
                          onClick={() => {
                            setMapExportPanelOpen(true);
                            window.setTimeout(() => {
                              mapRef.current?.invalidateSize(false);
                            }, 0);
                          }}
                          startIcon={<FileDownloadIcon />}
                          sx={MAP_TOOLBAR_BUTTON_SX}
                          disabled={mapImageDownloading || !map}
                        >
                          {mapImageDownloading
                            ? "画像を準備中..."
                            : "地図をダウンロード"}
                        </MuiButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                {canShowTradePriceList && (
                  <MuiButton
                    onClick={handleShowTradePriceList}
                    startIcon={<CurrencyExchangeIcon />}
                    sx={MAP_TOOLBAR_BUTTON_SX}
                  >
                    取引価格の一覧
                  </MuiButton>
                )}
                {/* 別のページへ移るものはボタンでなくリンクにする */}
                {!hasMapViewingPermission && (
                  <Link
                    href={PAID_PLAN_FORM_URL}
                    sx={{ ...MAP_CONTROL_TEXT_SX, ml: "auto", px: 1 }}
                  >
                    有料プランの契約はこちら
                  </Link>
                )}
              </Box>
            )}
            {/* 帯の下の残りを地図に使う */}
            <Box sx={MAP_AREA_SX}>
              <LeafletMap
                map={map}
                setMap={setMap}
                clickedAreaPolygonData={polygonData}
                polygonData={selectedPropertiesPolygonData}
                selectedPropertiesPinData={selectedPropertiesPinData}
                focusedPropertyPinPosition={focusedPropertyPinPosition}
                onListedPropertyPinClick={handleListedPropertyPinClick}
                viewLatLng={viewLatLng}
                printFramePreview={printFramePreview}
                mapExportPanelOpen={mapExportPanelOpen}
                setClickedLatLng={setClickedLatLng}
                markerState={markerState}
                receptionBookMarkerStates={receptionBookMarkerStates}
                selectedProperties={selectedProperties}
                setMarkerState={setMarkerState}
                setSelectedProperties={setSelectedProperties}
                checkDuplicateProperties={checkDuplicateProperties}
                findDuplicatePropertyIndex={findDuplicatePropertyIndex}
                mergePropertySelectionRow={mergePropertySelectionRow}
                buildLocationString={buildLocationString}
                setZoom={setZoom}
                realEstateSearchResult={displayRealEstateSearchResult}
                handleKaokuNumberSearch={handleKaokuNumberSearch}
                savedPlotData={displayPlotData}
                // 色とリスト名の対応は LoadedMapPlotBar にしか出ないので、色分けは CRM の時だけ
                savedPlotColorByRequestId={
                  crm ? savedPlotColorByRequestId : undefined
                }
                savedMapPlotData={savedMapPlotData}
                checkedLayers={checkedLayers}
                setCheckedLayers={setCheckedLayers}
                handlePinSave={handlePinSave}
                onTagsChange={handleTagsChange}
                onRequestedPicturesTagsChange={
                  handleRequestedPicturesTagsChange
                }
                onRequestedPicturesRemarksChange={
                  handleRequestedPicturesRemarksChange
                }
                onRequestedPicturesAdditionalFieldsChange={
                  handleRequestedPicturesAdditionalFieldsChange
                }
                onMapClickTagsUpdate={handleMapClickTagsUpdate}
                onMapClickRemarksUpdate={handleMapClickRemarksUpdate}
                onMapClickAdditionalFieldsUpdate={
                  handleMapClickAdditionalFieldsUpdate
                }
                setCurrentPopupRowId={setCurrentPopupRowId}
                currentPopupIdentifier={currentPopupIdentifier}
                mapRef={mapRef}
                onRealEstateSearchTagsChange={handleRealEstateSearchTagsChange}
                onRealEstateSearchRemarksChange={
                  handleRealEstateSearchRemarksChange
                }
                onRealEstateSearchAdditionalFieldsChange={
                  handleRealEstateSearchAdditionalFieldsChange
                }
                onRealEstateSearchPinClick={handleRealEstateSearchPinClick}
                loadRealEstateSearchLandAddress={
                  loadRealEstateSearchLandAddress
                }
                onReceptionBookTagsChange={handleReceptionBookTagsChange}
                onReceptionBookRemarksChange={handleReceptionBookRemarksChange}
                onReceptionBookAdditionalFieldsChange={
                  handleReceptionBookAdditionalFieldsChange
                }
                crm={crm as boolean}
                availableFields={availableFields}
                hasAvailableFields={hasAvailableFields}
                zenrinImageSelectionMode={zenrinImageSelectionMode}
                onZenrinSelectionBoundsChange={setZenrinImageSelectionBounds}
                zenrinImageOverlay={zenrinImageOverlay}
                hasMapViewingPermission={hasMapViewingPermission}
                kaokuNumberSearchFlag={true}
                realEstateDocumentFetchFlag={true}
                handleRealEstateDocumentFetchModal={
                  handleRealEstateDocumentFetchModal
                }
                propertySummary={true}
                handleNewPropertySheet={handleNewPropertySheet}
                registerOverlayController={registerOverlayController}
                searchChain={searchChain}
                tradePriceFilter={tradePriceTileFilter}
                onTradePriceFilterChange={setTradePriceTileFilter}
                onShowTradePriceDetailList={(location) => {
                  void handleShowTradePriceDetailList(location);
                }}
                onRegisterOpenReceptionBookPopup={(fn) => {
                  openReceptionBookPopupRef.current = fn;
                }}
              />
            </Box>
          </Box>
          {mapExportPanelOpen && (
            <Box sx={MAP_EXPORT_PANEL_COLUMN_SX}>
              <MapExportPanel
                open={mapExportPanelOpen}
                mapReady={Boolean(map)}
                downloading={mapImageDownloading}
                onClose={() => {
                  setMapExportPanelOpen(false);
                  window.setTimeout(() => {
                    mapRef.current?.invalidateSize(false);
                  }, 0);
                }}
                onCreatePreview={handleCreateMapImagePreview}
                onDownloadPng={async (options) =>
                  await handleDownloadMapImage("png", options)
                }
                onDownloadPdf={handleDownloadMapPdf}
                onApplyPrintScaleToMap={handleApplyPrintScaleToMap}
                onPrintFramePreviewChange={setPrintFramePreview}
                exportProgress={exportProgress}
              />
            </Box>
          )}
        </Box>
        {/* 物件一覧の拡大表示。一覧と物件リストの操作を大きな画面で使う */}
        <Dialog
          open={propertyListDialogOpen}
          onClose={(_event, reason) => {
            // タグ・備考を編集できる表を載せているので、外側のクリックや Esc では閉じない
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setPropertyListDialogOpen(false);
          }}
          fullWidth
          maxWidth="xl"
          // SP は画面いっぱいに広げて、表を少しでも広く見せる
          fullScreen={isMobile}
          PaperProps={{ sx: { height: { [MOBILE_BREAKPOINT]: "90vh" } } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            物件リスト（{selectedProperties.length}件）
            <IconButton
              aria-label="物件リストの拡大表示を閉じる"
              onClick={() => {
                setPropertyListDialogOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box sx={getGrayOutSx(!hasMapViewingPermission)}>
              {renderPropertyListActions("row")}
            </Box>
            <Box
              sx={{
                ...getGrayOutSx(!hasMapViewingPermission),
                flex: 1,
                minHeight: 0,
              }}
            >
              <PropertySelectionGrid
                rows={selectedProperties}
                setRows={setSelectedProperties}
                realEstateSearchResult={displayRealEstateSearchResult}
                crm={crm as boolean}
                height="100%"
                focusedRowId={focusedPropertyRowId}
                onRowFocus={handleFocusPropertyRow}
                bulkDeletable
              />
            </Box>
          </DialogContent>
        </Dialog>
        <Dialog
          open={acquireMultipleBooksModalIsOpen}
          onClose={(_event, reason) => {
            // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setAcquireMultipleBooksModalIsOpen(false);
          }}
          scroll={"paper"}
          fullWidth={true}
          maxWidth={"xl"}
          fullScreen={isMobile}
          aria-labelledby={"acquire-multiple-books-modal"}
          aria-describedby={"acquire-multiple-books-modal"}
        >
          <DialogActions disableSpacing={true}>
            <Box mx={2}>
              <Button
                label="閉じる"
                variant={ButtonVariantOption.Outlined}
                onClick={() => {
                  setAcquireMultipleBooksModalIsOpen(false);
                }}
                size={SizingWrapperStyle.SMALL}
                startIcon={<CloseIcon />}
              />
            </Box>
          </DialogActions>
          <AcquireMultipleBooksModal
            handleSubmit={acquireBookFormHandleSubmit}
            rows={acquireMultipleBooksRows}
          />
        </Dialog>
        <Dialog
          open={kaokuNumberSearchModalIsOpen}
          onClose={(_event, reason) => {
            // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setKaokuNumberSearchModalIsOpen(false);
          }}
          fullWidth={true}
          maxWidth={"xl"}
          fullScreen={isMobile}
          aria-labelledby={"kaoku-number-search-modal"}
          aria-describedby={"kaoku-number-search-modal"}
        >
          <ChibanKaokuNumberSearchModal
            variant={"map"}
            chibanList={kaokuNumberSearchChibanList}
            setKaokuNumberSearchModalIsOpen={setKaokuNumberSearchModalIsOpen}
            appendPropertySelectionRows={appendPropertySelectionRows}
          />
        </Dialog>
        {realEstateDocumentFetchModalIsOpen && (
          <RealEstateDocumentFetchModal
            open={realEstateDocumentFetchModalIsOpen}
            address={realEstateDocumentFetchModalAddress}
            chibanList={realEstateDocumentFetchModalChibanList}
            kaokuNumberList={realEstateDocumentFetchModalKaokuNumberList}
            onCancel={handleCloseRealEstateDocumentFetchModal}
            onClose={handleCloseRealEstateDocumentFetchModal}
          />
        )}
        <ZenrinRequestHistoryModal
          map={map}
          open={zenrinRequestHistoryModalIsOpen}
          onClose={() => {
            setZenrinRequestHistoryModalIsOpen(false);
          }}
          onApplyToMap={(row) => {
            void handleZenrinHistoryApply(row);
          }}
        />
      </Box>
      <NewPropertySummaryModal
        open={newPropertySummeryModalIsOpen}
        adi={newPropertySummaryAdi}
        handleClose={(
          _event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown" | null
        ) => {
          if (reason === "escapeKeyDown" || reason === "backdropClick") {
            return;
          }
          setNewPropertySummeryModalIsOpen(false);
        }}
      />
    </MapControlContext.Provider>
  );
};
